import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiSearch,
  HiPrinter,
  HiDocument,
  HiCloudUpload,
  HiKey,
  HiClipboardCopy,
  HiDotsVertical,
  HiCollection,
  HiEye,
  HiExternalLink,
  HiCash,
  HiCheckCircle,
  HiCamera,
  HiRefresh,
  HiLocationMarker,
  HiChevronLeft,
  HiChevronRight,
  HiDownload
} from 'react-icons/hi';
import Select from './Select';
import ClientSearchSelect from './ClientSearchSelect';
import * as HiIcons from 'react-icons/hi';
import { STATUS_LIST, getStatusInfo, getStatusColor, getStatusIcon } from '../utils/statusConfig';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import { SkeletonTable } from './Skeleton';
import { PERMISSAO_MAP } from '../types';
import apiService from '../services/api';
import { normalizeApiData, normalizeImageUrl } from '../utils/apiHelpers';
import {
  fetchAllPackageCodigos,
  nextPackageCodeForTipo,
} from '../utils/packageCodeSequence';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import './Boxes.css';

/** Retorna { date, time } para exibir em duas linhas, ou null se inválido */
const getBoxCreatedAtParts = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

/** Coluna Criado: data e hora em linhas separadas (alias usado na tabela) */
const formatBoxCreatedAt = (iso) => {
  const p = getBoxCreatedAtParts(iso);
  if (!p) return '—';
  return (
    <div className="boxes-table-cell-datetime__inner">
      <span>{p.date}</span>
      <span>{p.time}</span>
    </div>
  );
};

/** Chave estável do invoice na linha (para cor agrupada) */
const getBoxInvoiceKey = (box) => {
  const id =
    box?.invoice_id ?? box?.invoice?.id ?? box?.fatura_id ?? null;
  if (id != null && id !== '') return String(id);
  const codigoInv = box?.codigo_invoice ?? box?.invoice?.codigo_invoice;
  if (codigoInv != null && String(codigoInv).trim() !== '') {
    return String(codigoInv).trim();
  }
  return null;
};

/** Texto da coluna Invoice — prioriza `codigo_invoice` do retorno da API */
const getBoxInvoiceLabel = (box) => {
  const codigoInv = box?.codigo_invoice ?? box?.invoice?.codigo_invoice;
  if (codigoInv != null && String(codigoInv).trim() !== '') {
    return String(codigoInv).trim();
  }
  if (!getBoxInvoiceKey(box)) return null;
  const inv = box?.invoice;
  if (inv && typeof inv === 'object') {
    const s =
      inv.numero_documento ||
      inv.numero ||
      inv.codigo_rastreamento ||
      inv.codigo_invoice ||
      inv.codigo_rastreamento ||
      inv.title ||
      inv.tracking_code;
    if (s != null && String(s).trim() !== '') return String(s).trim();
    if (inv.id != null) return `#${inv.id}`;
  }
  const num = box?.invoice_numero ?? box?.numero_documento_invoice;
  if (num != null && String(num).trim() !== '') return String(num).trim();
  return `#${getBoxInvoiceKey(box)}`;
};

const INVOICE_ROW_PALETTE_SIZE = 8;

/** Map invoice_id -> classe CSS boxes-row--inv-N (ordem de primeira aparição na página) */
const buildInvoiceRowClassMap = (list) => {
  const map = new Map();
  let n = 0;
  if (!Array.isArray(list)) return map;
  for (const box of list) {
    const key = getBoxInvoiceKey(box);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, `boxes-row--inv-${n % INVOICE_ROW_PALETTE_SIZE}`);
      n += 1;
    }
  }
  return map;
};

const DESTINO_TEXT_PALETTE_SIZE = 8;

/** Chave estável para agrupar destinos iguais (ignora maiúsculas/minúsculas nas bordas) */
const getDestinoGroupKey = (box) => {
  const d = box?.destino;
  if (d == null || String(d).trim() === '') return '__empty__';
  return String(d).trim().toLowerCase();
};

/** Map destino normalizado -> classe de cor do texto (mesma cor para mesmo destino na página) */
const buildDestinoTextClassMap = (list) => {
  const map = new Map();
  let n = 0;
  if (!Array.isArray(list)) return map;
  for (const box of list) {
    const key = getDestinoGroupKey(box);
    if (!map.has(key)) {
      map.set(
        key,
        key === '__empty__'
          ? 'boxes-destino-text--empty'
          : `boxes-destino-text--${n % DESTINO_TEXT_PALETTE_SIZE}`
      );
      if (key !== '__empty__') n += 1;
    }
  }
  return map;
};

/** Valor padrão (cm) para dimensões em nova caixa/mala */
const DEFAULT_DIMENSAO_CM = '10';

/** ID da tarifa aplicada por padrão quando Gerente cria caixa/mala (sem alterar tarifa na UI). */
const DEFAULT_GERENTE_TARIFA_ID = 1;

/** Valor da remessa (R$/kg) × peso quando Gerente cria e não há valor preenchido/calculado. */
const GERENTE_VALOR_REMESA_POR_KG = 65;

const getClienteCodigoFromBox = (box) => {
  if (!box) return '';
  let codigos = box.codigos_acesso;
  if (typeof codigos === 'string' && codigos.trim() !== '') {
    try {
      codigos = JSON.parse(codigos);
    } catch (e) {
      codigos = null;
    }
  }
  const c = codigos?.cliente;
  return c != null && String(c).trim() !== '' ? String(c).trim() : '';
};

const Boxes = () => {
  const navigate = useNavigate();
  const { userRole, loading: permissionsLoading } = usePermissions();
  const isBoxesAdmin = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);
  const canCreateEditBoxes = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    const role = PERMISSAO_MAP[Number(userRole.permissaos_id)];
    return role === 'Admin' || role === 'Gerente';
  }, [userRole, permissionsLoading]);

  /** Etapas do wizard na barra de progresso (Gerente não vê etapa de Tarifas). */
  const wizardStepsDisplay = useMemo(
    () =>
      isBoxesAdmin
        ? [
            { step: 1, label: 'Básico' },
            { step: 2, label: 'Dimensões' },
            { step: 3, label: 'Tarifas' },
            { step: 4, label: 'Conteúdo' },
            { step: 5, label: 'Origem/Destino' },
            { step: 6, label: 'Destinatário' },
            { step: 7, label: 'Itens' },
            { step: 8, label: 'Nota Fiscal' },
          ]
        : [
            { step: 1, label: 'Básico' },
            { step: 2, label: 'Dimensões' },
            { step: 4, label: 'Conteúdo' },
            { step: 5, label: 'Origem/Destino' },
            { step: 6, label: 'Destinatário' },
            { step: 7, label: 'Itens' },
            { step: 8, label: 'Nota Fiscal' },
          ],
    [isBoxesAdmin]
  );

  const [boxes, setBoxes] = useState([]);
  const [clients, setClients] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(8);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalBoxes, setTotalBoxes] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [formData, setFormData] = useState({
    codigo: '',
    tipo: 'caixa',
    cliente_id: '',
    cliente_nome: '',
    peso: '',
    dimensoes: {
      comprimento: DEFAULT_DIMENSAO_CM,
      largura: DEFAULT_DIMENSAO_CM,
      altura: DEFAULT_DIMENSAO_CM
    },
    valor_remessa: '',
    tarifa_id: '',
    tarifa_intermediacao: '',
    tarifa_coleta: '',
    conteudo: '',
    observacao: '',
    status: 'pedido_criado',
    origem: '',
    destino: '',
    nome_destinatario: '',
    telefone_destinatario: '',
    pais_transito: '',
    codigo_rastreio: '',
    codigo_atualizacao: '',
    codigos_acesso: {
      cliente: ''
    },
    itens: [],
    nota_fiscal: null
  });
  const [editingId, setEditingId] = useState(null);
  const [currentItem, setCurrentItem] = useState({
    nome: '',
    ncm: '',
    descricao: '',
    peso: '',
    preco: '',
    quantidade: 1,
    fotos: [] // Inicializado como array vazio
  });
  const [ncmSuggestions, setNcmSuggestions] = useState([]);
  const [showNcmDropdown, setShowNcmDropdown] = useState(false);
  const [invalidFields, setInvalidFields] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [boxToDelete, setBoxToDelete] = useState(null);
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [selectedBoxCodes, setSelectedBoxCodes] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  /** Todos os codigo_pacote já carregados — sequência por prefixo (CX/ML/PL/CT) */
  const packageCodesRef = useRef([]);
  const [loadingSequenceCodes, setLoadingSequenceCodes] = useState(false);
  const [coletasForModal, setColetasForModal] = useState([]);
  const [selectedColetaId, setSelectedColetaId] = useState('');
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedBoxItems, setSelectedBoxItems] = useState(null);
  const [showNotaFiscalModal, setShowNotaFiscalModal] = useState(false);
  const [selectedNotaFiscal, setSelectedNotaFiscal] = useState(null);
  const [showDeleteItemDialog, setShowDeleteItemDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewPaymentModal, setShowViewPaymentModal] = useState(false);
  const [showCancelPaymentModal, setShowCancelPaymentModal] = useState(false);
  const [cancelJustification, setCancelJustification] = useState('');
  const [selectedBoxForPayment, setSelectedBoxForPayment] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedBoxDelivery, setSelectedBoxDelivery] = useState(null);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [selectedBoxRecipient, setSelectedBoxRecipient] = useState(null);
  const [savingRecipientImage, setSavingRecipientImage] = useState(false);
  const [boxPayment, setBoxPayment] = useState({
    valor: '',
    data: '',
    metodo: 'dinheiro',
    comprovante: null,
  });
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraForItem, setCameraForItem] = useState(false); // true se for para item, false se for para comprovante
  const [cameraForNotaFiscal, setCameraForNotaFiscal] = useState(false); // true se for para nota fiscal
  const [cameraForComprovante, setCameraForComprovante] = useState(false); // true se for para comprovante
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' = traseira, 'user' = frontal
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const modalFormRef = useRef(null);
  const wizardProgressRef = useRef(null);
  const recipientPrintAreaRef = useRef(null);
  const { notifications, removeNotification, showSuccess, showWarning } = useNotification();

  // Ao mudar de etapa no wizard: rolar o conteúdo do modal para o topo e destacar a aba atual (rolagem horizontal em mobile/tablet)
  useEffect(() => {
    if (showModal && modalFormRef.current) {
      modalFormRef.current.scrollTop = 0;
    }
    if (showModal && wizardProgressRef.current) {
      const activeEl = wizardProgressRef.current.querySelector('.wizard-step.active');
      if (activeEl) {
        const t = setTimeout(() => {
          activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 50);
        return () => clearTimeout(t);
      }
    }
  }, [currentStep, showModal]);

  useEffect(() => {
    loadBoxes();
    loadClients();
    loadTarifas();
  }, []);

  // Gerente não usa etapa 3; evita ficar preso nela ao alternar perfil ou estado herdado
  useEffect(() => {
    if (showModal && !isBoxesAdmin && currentStep === 3) {
      setCurrentStep(4);
    }
  }, [showModal, isBoxesAdmin, currentStep]);

  // Busca com debounce: ao digitar, recarrega a lista pela API após 400ms
  const searchInitialMount = useRef(true);
  useEffect(() => {
    if (searchInitialMount.current) {
      searchInitialMount.current = false;
      return;
    }
    const t = setTimeout(() => {
      setCurrentPage(1);
      loadBoxes({ page: 1, search: searchTerm });
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fechar menu quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.actions-menu-wrapper')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Fechar dropdown de NCM ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNcmDropdown && !event.target.closest('.ncm-dropdown') && !event.target.closest('input[placeholder="1234.56.78"]')) {
        setShowNcmDropdown(false);
      }
    };

    if (showNcmDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNcmDropdown]);

  const loadClients = async () => {
    try {
      const perPage = 500;
      let page = 1;
      const all = [];
      for (;;) {
        const response = await apiService.getClientsList({
          page,
          per_page: perPage,
        });
        const list = normalizeApiData(response);
        if (!Array.isArray(list) || list.length === 0) break;
        all.push(...list);
        const lastPage = response?.last_page ?? response?.lastPage;
        if (lastPage !== undefined && lastPage !== null) {
          if (page >= Number(lastPage)) break;
          page += 1;
          continue;
        }
        if (list.length < perPage) break;
        page += 1;
      }
      setClients(
        all.map((c) => ({ id: c.id, nome: c.name || c.nome || '' }))
      );
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    }
  };

  const loadTarifas = async () => {
    try {
      const data = await apiService.getTarifas();
      const tarifasList = normalizeApiData(data);
      setTarifas(Array.isArray(tarifasList) ? tarifasList : []);
    } catch (error) {
      console.error('Erro ao carregar tarifas:', error);
      setTarifas([]);
    }
  };

  const loadBoxes = async (overrides = {}) => {
    const page = overrides.page !== undefined ? overrides.page : currentPage;
    const perPage = overrides.per_page !== undefined ? overrides.per_page : itemsPerPage;
    const search = overrides.search !== undefined ? overrides.search : searchTerm;
    try {
      const response = await apiService.getBoxes({
        page,
        per_page: perPage,
        ...(search && search.trim() ? { search: search.trim() } : {})
      });
      const boxesList = normalizeApiData(response);
      // Mapear os campos da API para o formato esperado pelo componente
      const boxesWithDefaults = boxesList.map(box => ({
          ...box,
        // Mapear codigo_pacote para codigo
        codigo: box.codigo_pacote || box.codigo || '',
        // Mapear cliente.nome para cliente_nome (verificar múltiplas possibilidades)
        cliente_nome: box.cliente?.nome ||
                     box.cliente?.name ||
                     box.cliente_nome ||
                     box.cliente_name ||
                     (box.cliente && typeof box.cliente === 'string' ? box.cliente : '') ||
                     '',
        // Mapear cliente_id
        cliente_id: box.cliente_id || box.cliente?.id || '',
        // Mapear dimensões separadas para objeto dimensoes
        dimensoes: {
          comprimento: box.comprimento || box.dimensoes?.comprimento || '',
          largura: box.largura || box.dimensoes?.largura || '',
          altura: box.altura || box.dimensoes?.altura || ''
        },
        // Mapear peso_bruto para peso e formatar com ponto
        // Se o peso vier como número, converter para o formato da máscara (multiplicar por 100)
        peso: (() => {
          const pesoValue = box.peso_bruto || box.peso;
          if (!pesoValue) return '';
          // Se for número, multiplicar por 100 para converter para o formato da máscara
          if (typeof pesoValue === 'number') {
            return formatWeight(String(Math.round(pesoValue * 100)));
          }
          // Se for string, converter vírgula para ponto e formatar
          const pesoStr = String(pesoValue).replace(',', '.');
          const pesoNum = parseFloat(pesoStr);
          if (!isNaN(pesoNum)) {
            return formatWeight(String(Math.round(pesoNum * 100)));
          }
          return formatWeight(pesoStr);
        })(),
        // Mapear valor_declarado para valor_remessa
        valor_remessa: box.valor_declarado || box.valor_remessa || '',
        // Mapear observacao
        observacao: box.observacao || '',
        // Preservar status_pagamento da API
        status_pagamento: box.status_pagamento || null,
        // Garantir que codigos_acesso existe e está no formato correto
          codigos_acesso: (() => {
            let codigos = box.codigos_acesso;
            // Se for string JSON, fazer parse
            if (typeof codigos === 'string' && codigos.trim() !== '') {
              try {
                codigos = JSON.parse(codigos);
              } catch (e) {
                console.error('Erro ao fazer parse de codigos_acesso:', e);
                codigos = null;
              }
            }
            // Garantir formato padrão com código do cliente
            return codigos && typeof codigos === 'object' ? {
              cliente: codigos.cliente || ''
            } : {
              cliente: ''
            };
          })(),
        // Garantir que itens existe
        itens: box.itens || [],
        // Mapear nota_fiscal_url para nota_fiscal
        nota_fiscal: box.nota_fiscal_url || box.nota_fiscal || null,
        // Preservar status da API
        status: box.status || 'pedido_criado',
        invoice_id: box.invoice_id ?? box.invoice?.id ?? box.fatura_id ?? null,
        codigo_invoice:
          box.codigo_invoice ??
          box.invoice?.codigo_invoice ??
          null,
        }));
      setBoxes(boxesWithDefaults);
      setTotalBoxes(response.total ?? 0);
      setLastPage(response.last_page ?? 1);
      if (overrides.page !== undefined) setCurrentPage(overrides.page);
      if (overrides.per_page !== undefined) setItemsPerPage(overrides.per_page);
      // Não chamar setSearchTerm a partir de overrides.search: a busca é debounced e
      // respostas fora de ordem reaplicariam um termo antigo e apagariam o que o usuário digitou.
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar boxes:', error);
      setBoxes([]);
      setTotalBoxes(0);
      setLastPage(1);
      setLoading(false);
    }
  };

  const loadAllCodesForSequence = () =>
    fetchAllPackageCodigos((params) => apiService.getBoxes(params));

  // Função para gerar código de acesso único
  const generateAccessCode = (prefix = '') => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = prefix;
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Função para gerar código de acesso do cliente
  const generateClientAccessCode = () => {
    return {
      cliente: generateAccessCode('CLI')
    };
  };

  // Função para formatar valor como moeda brasileira
  const formatCurrency = (value) => {
    if (!value) return '';
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    if (numbers === '') return '';
    // Converte para número e formata
    const number = parseFloat(numbers) / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };

  // Função para formatar peso com ponto como separador decimal (similar à máscara de moeda)
  const formatWeight = (value) => {
    if (!value) return '';
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    if (numbers === '') return '';
    // Converte para número e divide por 100 (similar à moeda)
    const number = parseFloat(numbers) / 100;
    // Formata com ponto como separador decimal e 2 casas decimais
    return number.toFixed(2).replace(',', '.');
  };

  // Função para exibir valor formatado
  const displayCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    // Se já está formatado, retorna
    if (typeof value === 'string' && value.includes('R$')) {
      return value;
    }
    // Converte número para formato brasileiro
    const number = typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
      : value || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };

  // Função para converter peso de string para número
  const parseWeight = (weightStr) => {
    if (!weightStr) return 0;
    // Remove espaços e converte vírgula para ponto
    const cleaned = weightStr.toString().trim().replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Função para validar e destacar campos inválidos
  const validateAndHighlightField = (fieldName, message, stepNumber = null) => {
    // Limpar validações anteriores
    setInvalidFields({});

    // Destacar campo inválido
    setTimeout(() => {
      setInvalidFields({ [fieldName]: true });

      // Navegar até o campo
      const fieldId = `field-${fieldName}`;
      const fieldElement = document.getElementById(fieldId) ||
                          document.querySelector(`[name="${fieldName}"]`) ||
                          document.querySelector(`input[placeholder*="${fieldName}"]`);

      if (fieldElement) {
        // Scroll até o campo
        fieldElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // Focar no campo
        setTimeout(() => {
          fieldElement.focus();
          if (fieldElement.select) {
            fieldElement.select();
          }
        }, 300);
      } else if (stepNumber) {
        // Se não encontrou o campo, navegar até o step do wizard
        setCurrentStep(stepNumber);
        setTimeout(() => {
          const stepElement = document.querySelector(`.wizard-step-content.active`);
          if (stepElement) {
            stepElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
          // Tentar encontrar o campo novamente após mudar de step
          setTimeout(() => {
            const fieldElementAfterStep = document.getElementById(fieldId) ||
                                        document.querySelector(`[name="${fieldName}"]`);
            if (fieldElementAfterStep) {
              fieldElementAfterStep.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              setTimeout(() => {
                fieldElementAfterStep.focus();
                if (fieldElementAfterStep.select) {
                  fieldElementAfterStep.select();
                }
              }, 200);
            }
          }, 300);
        }, 100);
      }
    }, 100);

    // Mostrar mensagem de erro
    showWarning(message);
  };

  // Função para buscar NCMs baseado no nome do item
  const searchNcmByProductName = (productName) => {
    if (!productName || productName.trim().length < 2) {
      return [];
    }

    const nameLower = productName.toLowerCase().trim();

    // Mapeamento de palavras-chave para NCMs comuns
    const ncmMap = {
      // Roupas e Calçados
      'camisa': ['6109.10.00', '6109.90.00', '6205.20.00'],
      'camiseta': ['6109.10.00', '6109.90.00'],
      'calça': ['6203.42.00', '6203.43.00', '6203.49.00'],
      'bermuda': ['6203.42.00', '6203.49.00'],
      'short': ['6203.42.00', '6203.49.00'],
      'vestido': ['6204.42.00', '6204.43.00', '6204.44.00'],
      'sapato': ['6403.99.00', '6404.19.00'],
      'tênis': ['6404.11.00', '6404.19.00'],
      'chinelo': ['6402.20.00', '6402.99.00'],
      'sandália': ['6402.20.00', '6402.99.00'],
      'bolsa': ['4202.12.00', '4202.22.00', '4202.32.00'],
      'mochila': ['4202.12.00', '4202.92.00'],
      'cinto': ['4203.30.00'],

      // Eletrônicos
      'celular': ['8517.12.00', '8517.70.90'],
      'smartphone': ['8517.12.00', '8517.70.90'],
      'telefone': ['8517.12.00', '8517.70.90'],
      'tablet': ['8471.30.00', '8517.12.00'],
      'notebook': ['8471.30.00'],
      'computador': ['8471.30.00', '8471.50.00'],
      'fone': ['8518.30.00', '8518.50.00'],
      'fone de ouvido': ['8518.30.00', '8518.50.00'],
      'carregador': ['8504.40.00', '8504.50.00'],
      'cabo': ['8544.42.00', '8544.49.00'],

      // Cosméticos e Perfumaria
      'perfume': ['3303.00.00'],
      'colônia': ['3303.00.00'],
      'shampoo': ['3305.10.00', '3305.90.00'],
      'condicionador': ['3305.10.00', '3305.90.00'],
      'sabonete': ['3401.11.00', '3401.19.00'],
      'creme': ['3304.99.00', '3304.91.00'],
      'maquiagem': ['3304.20.00', '3304.30.00'],
      'batom': ['3304.20.00'],
      'esmalte': ['3304.30.00'],

      // Alimentos
      'chocolate': ['1806.31.00', '1806.32.00', '1806.90.00'],
      'café': ['0901.11.00', '0901.12.00', '0901.21.00'],
      'açúcar': ['1701.14.00', '1701.99.00'],
      'arroz': ['1006.30.00', '1006.40.00'],
      'feijão': ['0713.32.00', '0713.33.00'],
      'óleo': ['1507.90.00', '1509.90.00'],
      'azeite': ['1509.90.00'],

      // Medicamentos
      'medicamento': ['3004.90.00', '3004.50.00'],
      'remédio': ['3004.90.00', '3004.50.00'],
      'vitamina': ['2936.27.00', '2936.29.00'],
      'suplemento': ['2106.90.00', '2936.27.00'],

      // Brinquedos
      'brinquedo': ['9503.00.00'],
      'boneca': ['9503.00.00'],
      'carrinho': ['9503.00.00'],
      'jogo': ['9504.30.00', '9504.90.00'],

      // Livros e Papelaria
      'livro': ['4901.10.00', '4901.99.00'],
      'caderno': ['4820.20.00', '4820.30.00'],
      'caneta': ['9608.10.00', '9608.20.00'],
      'lápis': ['9609.10.00', '9609.20.00'],

      // Eletrodomésticos
      'geladeira': ['8418.10.00', '8418.21.00'],
      'fogão': ['7321.11.00', '7321.12.00'],
      'microondas': ['8516.50.00'],
      'liquidificador': ['8509.40.00'],
      'ventilador': ['8414.51.00', '8414.59.00'],

      // Ferramentas
      'ferramenta': ['8205.40.00', '8205.51.00'],
      'chave': ['8204.11.00', '8204.12.00'],
      'martelo': ['8205.20.00'],
      'chave de fenda': ['8205.40.00'],

      // Material de Construção
      'cimento': ['2523.29.00'],
      'tijolo': ['6904.10.00', '6904.90.00'],
      'telha': ['6905.10.00', '6905.90.00'],

      // Outros
      'óculos': ['9004.10.00', '9004.90.00'],
      'relógio': ['9102.11.00', '9102.12.00', '9102.19.00'],
      'joia': ['7113.19.00', '7113.20.00'],
      'bijuteria': ['7117.19.00', '7117.90.00'],
      'cigarro': ['2402.20.00'],
      'bebida': ['2203.00.00', '2208.20.00'],
      'cerveja': ['2203.00.00'],
      'vinho': ['2204.21.00', '2204.29.00']
    };

    // Buscar NCMs que correspondem às palavras-chave
    const suggestions = [];
    const words = nameLower.split(/\s+/);

    for (const [keyword, ncms] of Object.entries(ncmMap)) {
      if (nameLower.includes(keyword) || words.some(word => word.includes(keyword) || keyword.includes(word))) {
        ncms.forEach(ncm => {
          if (!suggestions.includes(ncm)) {
            suggestions.push(ncm);
          }
        });
      }
    }

    // Se não encontrou sugestões específicas, retornar NCMs genéricos comuns
    if (suggestions.length === 0) {
      return [
        '8471.30.00', // Máquinas automáticas para processamento de dados
        '8517.12.00', // Telefones celulares
        '6404.19.00', // Calçados com solas de borracha ou plástico
        '4202.12.00', // Bolsas de mão
        '3303.00.00', // Perfumes e águas de colônia
        '6109.10.00', // Camisetas de malha
        '6203.42.00', // Calças de algodão
        '3004.90.00'  // Medicamentos
      ];
    }

    return suggestions.slice(0, 10); // Limitar a 10 sugestões
  };

  // Função para atualizar sugestões de NCM quando o nome do item muda
  const handleItemNameChange = (e) => {
    const newName = e.target.value;
    setCurrentItem({ ...currentItem, nome: newName });

    if (newName && newName.trim().length >= 2) {
      const suggestions = searchNcmByProductName(newName);
      setNcmSuggestions(suggestions);
      setShowNcmDropdown(suggestions.length > 0);
    } else {
      setNcmSuggestions([]);
      setShowNcmDropdown(false);
    }
  };

  // Função para converter valor de moeda formatado para número
  const parseCurrency = (currencyStr) => {
    if (!currencyStr) return 0;
    // Remove R$, espaços e converte formato brasileiro para número
    const cleaned = currencyStr.toString()
      .replace('R$', '')
      .replace(/\./g, '') // Remove pontos (separadores de milhar)
      .replace(',', '.') // Converte vírgula decimal para ponto
      .trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Função para formatar número como moeda brasileira (sem dividir por 100)
  const formatNumberAsCurrency = (value) => {
    const number = typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
      : value || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };

  // Função para calcular tarifa baseada no peso
  const calculateTarifa = (tarifaId, peso, tarifaIntermediacaoAtual = '', tarifaColetaAtual = '') => {
    if (!tarifaId) {
      return { tarifa_intermediacao: '', tarifa_coleta: '', valor_remessa: '' };
    }

    const tarifa = tarifas.find(t => t.id === tarifaId || t.id === parseInt(tarifaId));
    if (!tarifa) {
      return { tarifa_intermediacao: '', tarifa_coleta: '', valor_remessa: '' };
    }

    // Se não há peso informado, retornar valores vazios mas manter tarifa selecionada
    if (!peso) {
      return { tarifa_intermediacao: '', tarifa_coleta: '', valor_remessa: '' };
    }

    const pesoNum = parseWeight(peso);
    const valorTarifa = parseFloat(tarifa.valor) || 0;
    const acrescimentoEmbalagem = parseFloat(tarifa.acrescimento_embalagem) || 0;

    // Converter valores atuais de tarifa_intermediacao e tarifa_coleta para números
    const tarifaIntermediacaoNum = parseCurrency(tarifaIntermediacaoAtual);
    const tarifaColetaNum = parseCurrency(tarifaColetaAtual);

    // Sempre calcular valor base: valor_tarifa * peso (independente de ser maior ou menor que kg_max)
    const valorBase = valorTarifa * pesoNum;

    // Calcular valor total: valor_base + acrescimento_embalagem + tarifa_intermediacao + tarifa_coleta
    const valorTotal = valorBase + acrescimentoEmbalagem + tarifaIntermediacaoNum + tarifaColetaNum;
    const valorTotalFormatado = formatNumberAsCurrency(valorTotal);

    return {
      tarifa_intermediacao: tarifaIntermediacaoAtual || '',
      tarifa_coleta: tarifaColetaAtual || '',
      valor_remessa: valorTotalFormatado || ''
    };
  };

  // Função para lidar com mudança de tarifa
  const handleTarifaChange = (tarifaId) => {
    if (!isBoxesAdmin) return;
    // Validar se peso foi informado
    if (tarifaId && !formData.peso) {
      showWarning('Por favor, informe o peso antes de selecionar uma tarifa');
      return;
    }

    // Buscar tarifa selecionada
    const tarifaSelecionada = tarifas.find(t => t.id === tarifaId || t.id === parseInt(tarifaId));

    // Calcular tarifas automaticamente, passando valores atuais
    const tarifasCalculadas = calculateTarifa(
      tarifaId,
      formData.peso,
      formData.tarifa_intermediacao,
      formData.tarifa_coleta
    );

    // Preparar dados do formulário
    const newFormData = {
      ...formData,
      tarifa_id: tarifaId,
      tarifa_intermediacao: tarifasCalculadas.tarifa_intermediacao,
      tarifa_coleta: tarifasCalculadas.tarifa_coleta,
      // Sempre atualizar valor_remessa quando houver cálculo (mesmo que seja string vazia)
      valor_remessa: tarifasCalculadas.valor_remessa !== undefined ? tarifasCalculadas.valor_remessa : formData.valor_remessa
    };

    // Se uma tarifa foi selecionada, preencher origem e destino
    if (tarifaSelecionada) {
      newFormData.origem = tarifaSelecionada.origem || '';
      newFormData.destino = tarifaSelecionada.destino || '';
    } else {
      // Se nenhuma tarifa foi selecionada, limpar origem e destino
      newFormData.origem = '';
      newFormData.destino = '';
    }

    setFormData(newFormData);
  };

  // Função para lidar com mudança de peso
  const handlePesoChange = (peso) => {
    const formatted = formatWeight(peso);
    const newFormData = { ...formData, peso: formatted };

    // Se há uma tarifa selecionada, recalcular
    // Usar o peso formatado (já está com ponto como separador decimal)
    if (formData.tarifa_id) {
      const tarifasCalculadas = calculateTarifa(
        formData.tarifa_id,
        formatted,
        formData.tarifa_intermediacao,
        formData.tarifa_coleta
      );
      newFormData.tarifa_intermediacao = tarifasCalculadas.tarifa_intermediacao;
      newFormData.tarifa_coleta = tarifasCalculadas.tarifa_coleta;
      // Sempre atualizar valor_remessa quando houver cálculo (mesmo que seja string vazia)
      newFormData.valor_remessa = tarifasCalculadas.valor_remessa !== undefined ? tarifasCalculadas.valor_remessa : newFormData.valor_remessa;
      if (!isBoxesAdmin && formatted) {
        const pesoW = parseWeight(formatted);
        if (
          pesoW > 0 &&
          (!String(newFormData.valor_remessa).trim() ||
            parseCurrency(newFormData.valor_remessa) <= 0)
        ) {
          newFormData.valor_remessa = formatNumberAsCurrency(
            GERENTE_VALOR_REMESA_POR_KG * pesoW
          );
        }
      }
    }

    setFormData(newFormData);
  };

  // Função para recalcular valor_remessa quando tarifa_intermediacao ou tarifa_coleta mudarem
  const recalculateValorRemessa = (tarifaIntermediacao, tarifaColeta) => {
    // Só recalcular se há tarifa selecionada e peso informado
    if (!formData.tarifa_id || !formData.peso) {
      return formData.valor_remessa;
    }

    const tarifa = tarifas.find(t => t.id === formData.tarifa_id || t.id === parseInt(formData.tarifa_id));
    if (!tarifa) {
      return formData.valor_remessa;
    }

    const pesoNum = parseWeight(formData.peso);
    const valorTarifa = parseFloat(tarifa.valor) || 0;
    const acrescimentoEmbalagem = parseFloat(tarifa.acrescimento_embalagem) || 0;

    // Sempre calcular valor base: valor_tarifa * peso (independente de ser maior ou menor que kg_max)
    const valorBase = valorTarifa * pesoNum;

    const tarifaIntermediacaoNum = parseCurrency(tarifaIntermediacao);
    const tarifaColetaNum = parseCurrency(tarifaColeta);

    // Calcular valor total: valor_base + acrescimento_embalagem + tarifa_intermediacao + tarifa_coleta
    const valorTotal = valorBase + acrescimentoEmbalagem + tarifaIntermediacaoNum + tarifaColetaNum;
    return formatNumberAsCurrency(valorTotal);
  };

  // Função para lidar com mudança de tarifa_intermediacao
  const handleTarifaIntermediacaoChange = (value) => {
    if (!isBoxesAdmin) return;
    const formatted = formatCurrency(value);
    const novoValorRemessa = recalculateValorRemessa(formatted, formData.tarifa_coleta);
    setFormData({
      ...formData,
      tarifa_intermediacao: formatted,
      valor_remessa: novoValorRemessa
    });
  };

  // Função para lidar com mudança de tarifa_coleta
  const handleTarifaColetaChange = (value) => {
    if (!isBoxesAdmin) return;
    const formatted = formatCurrency(value);
    const novoValorRemessa = recalculateValorRemessa(formData.tarifa_intermediacao, formatted);
    setFormData({
      ...formData,
      tarifa_coleta: formatted,
      valor_remessa: novoValorRemessa
    });
  };

  const loadColetasForModal = async () => {
    try {
      const response = await apiService.getColetas({ page: 1, per_page: 200 });
      const list = normalizeApiData(response);
      const mapped = (Array.isArray(list) ? list : []).map((row) => ({
        ...row,
        codigo: row.codigo_pacote || row.codigo || '',
        cliente_nome:
          row.cliente_nome ||
          row.cliente?.nome ||
          row.cliente?.name ||
          row.cliente_name ||
          '',
      }));
      setColetasForModal(mapped);
    } catch (e) {
      console.error('Erro ao carregar coletas:', e);
      setColetasForModal([]);
    }
  };

  const applyFormPrefillFromColeta = (coleta) => {
    setFormData((prev) => {
      const pesoRaw = coleta.peso_bruto ?? coleta.peso;
      let pesoFormatted = '';
      if (pesoRaw !== null && pesoRaw !== undefined && pesoRaw !== '') {
        const n =
          typeof pesoRaw === 'number'
            ? pesoRaw
            : parseFloat(String(pesoRaw).replace(',', '.'));
        if (!Number.isNaN(n) && n > 0) {
          pesoFormatted = formatWeight(String(Math.round(n * 100)));
        }
      }

      const clienteNome =
        coleta.cliente_nome ||
        coleta.cliente?.nome ||
        coleta.cliente?.name ||
        clients.find((c) => String(c.id) === String(coleta.cliente_id))?.nome ||
        '';

      let codigos = coleta.codigos_acesso;
      if (typeof codigos === 'string' && codigos.trim() !== '') {
        try {
          codigos = JSON.parse(codigos);
        } catch {
          codigos = null;
        }
      }
      const codigosAcesso =
        codigos && typeof codigos === 'object' && codigos.cliente
          ? { cliente: codigos.cliente }
          : prev.codigos_acesso;

      const tarifaId = coleta.tarifa_id ? String(coleta.tarifa_id) : '';

      const next = {
        ...prev,
        cliente_id:
          coleta.cliente_id != null && coleta.cliente_id !== ''
            ? String(coleta.cliente_id)
            : prev.cliente_id,
        cliente_nome: clienteNome || prev.cliente_nome,
        origem: coleta.origem != null ? String(coleta.origem) : '',
        destino: coleta.destino != null ? String(coleta.destino) : '',
        peso: pesoFormatted,
        tarifa_id: tarifaId,
        nome_destinatario: coleta.nome_destinatario || '',
        telefone_destinatario: coleta.telefone_destinatario || '',
        codigos_acesso: codigosAcesso,
      };

      if (tarifaId && pesoFormatted) {
        const t = calculateTarifa(
          tarifaId,
          pesoFormatted,
          next.tarifa_intermediacao || '',
          next.tarifa_coleta || ''
        );
        next.valor_remessa = t.valor_remessa;
        next.tarifa_intermediacao = t.tarifa_intermediacao;
        next.tarifa_coleta = t.tarifa_coleta;
      } else if (tarifaId) {
        const tarifa = tarifas.find(
          (x) => String(x.id) === String(tarifaId)
        );
        if (tarifa) {
          next.origem = tarifa.origem || next.origem;
          next.destino = tarifa.destino || next.destino;
        }
        next.valor_remessa = '';
      } else {
        const vr = coleta.valor_remessa ?? coleta.valor_declarado;
        if (vr !== null && vr !== undefined && vr !== '') {
          const num =
            typeof vr === 'number'
              ? vr
              : parseFloat(String(vr).replace(/[^\d,.-]/g, '').replace(',', '.'));
          if (!Number.isNaN(num)) {
            next.valor_remessa = formatNumberAsCurrency(num);
          }
        }
      }

      if (!isBoxesAdmin) {
        const fixedId = String(DEFAULT_GERENTE_TARIFA_ID);
        next.tarifa_id = fixedId;
        next.tarifa_intermediacao = '';
        next.tarifa_coleta = '';
        if (pesoFormatted) {
          const t = calculateTarifa(fixedId, pesoFormatted, '', '');
          next.valor_remessa = t.valor_remessa;
          next.tarifa_intermediacao = t.tarifa_intermediacao;
          next.tarifa_coleta = t.tarifa_coleta;
        } else {
          next.valor_remessa = '';
        }
        const tf = tarifas.find((x) => String(x.id) === fixedId);
        if (tf) {
          next.origem = tf.origem || '';
          next.destino = tf.destino || '';
        }
      }

      return next;
    });
  };

  const handleColetaPrefillChange = (value) => {
    setSelectedColetaId(value);
    if (!value) return;
    const coleta = coletasForModal.find((c) => String(c.id) === String(value));
    if (coleta) applyFormPrefillFromColeta(coleta);
  };

  const handleOpenModal = async () => {
    setLoadingSequenceCodes(true);
    try {
      const codes = await loadAllCodesForSequence();
      packageCodesRef.current = codes;
      const newCode = nextPackageCodeForTipo(codes, 'caixa');
      const accessCodes = generateClientAccessCode();
      setFormData({
        codigo: newCode,
        tipo: 'caixa',
        cliente_id: '',
        cliente_nome: '',
        peso: '',
        dimensoes: {
          comprimento: DEFAULT_DIMENSAO_CM,
          largura: DEFAULT_DIMENSAO_CM,
          altura: DEFAULT_DIMENSAO_CM
        },
        valor_remessa: '',
        tarifa_id: isBoxesAdmin ? '' : String(DEFAULT_GERENTE_TARIFA_ID),
        tarifa_intermediacao: '',
        tarifa_coleta: '',
        conteudo: '',
        observacao: '',
        status: 'pedido_criado',
        origem: '',
        destino: '',
        nome_destinatario: '',
        telefone_destinatario: '',
        pais_transito: '',
        codigo_rastreio: '',
        codigo_atualizacao: '',
        codigos_acesso: accessCodes,
        itens: [],
        nota_fiscal: null
      });
      setSelectedColetaId('');
      setEditingId(null);
      setShowModal(true);
      void loadColetasForModal();
    } catch (e) {
      showWarning(
        e?.message || 'Não foi possível obter o próximo código. Tente novamente.'
      );
    } finally {
      setLoadingSequenceCodes(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep <= 1) return;
    if (!isBoxesAdmin && currentStep === 4) {
      setCurrentStep(2);
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  /** Asterisco visual para rótulos obrigatórios no wizard */
  const labelReq = (
    <span className="label-required" aria-hidden="true">
      *
    </span>
  );

  const scrollToWizardField = (fieldId) => {
    setTimeout(() => {
      const el =
        document.getElementById(fieldId) ||
        document.querySelector(`[name="${fieldId}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el.focus) el.focus();
      }
    }, 150);
  };

  const getWizardStepValidation = (step) => {
    const invalid = {};
    const missingLabels = [];

    if (step === 1) {
      if (!formData.cliente_id) {
        invalid.cliente_id = true;
        missingLabels.push('Cliente');
      }
    } else if (step === 2) {
      if (!formData.peso || !String(formData.peso).trim()) {
        invalid.peso = true;
        missingLabels.push('Peso bruto');
      } else if (parseWeight(formData.peso) <= 0) {
        invalid.peso = true;
        missingLabels.push('Peso bruto (deve ser maior que zero)');
      }
      const dimKeys = [
        ['comprimento', 'Comprimento (cm)'],
        ['largura', 'Largura (cm)'],
        ['altura', 'Altura (cm)'],
      ];
      dimKeys.forEach(([key, label]) => {
        const v = formData.dimensoes?.[key];
        if (!v || !String(v).trim()) {
          invalid[`dim_${key}`] = true;
          missingLabels.push(label);
        } else {
          const n = parseFloat(String(v).replace(',', '.'));
          if (Number.isNaN(n) || n <= 0) {
            invalid[`dim_${key}`] = true;
            missingLabels.push(`${label} (valor inválido)`);
          }
        }
      });
    } else if (step === 3) {
      if (!formData.valor_remessa || !String(formData.valor_remessa).trim()) {
        invalid.valor_remessa = true;
        missingLabels.push('Valor da remessa');
      } else if (parseCurrency(formData.valor_remessa) <= 0) {
        invalid.valor_remessa = true;
        missingLabels.push('Valor da remessa (deve ser maior que zero)');
      }
    } else if (step === 4) {
      if (!formData.conteudo || !String(formData.conteudo).trim()) {
        invalid.conteudo = true;
        missingLabels.push('Conteúdo');
      }
    } else if (step === 5) {
      if (!formData.origem || !String(formData.origem).trim()) {
        invalid.origem = true;
        missingLabels.push('Origem');
      }
      if (!formData.destino || !String(formData.destino).trim()) {
        invalid.destino = true;
        missingLabels.push('Destino');
      }
    }

    return {
      valid: missingLabels.length === 0,
      missingLabels,
      invalid,
    };
  };

  const clearInvalidKeysForStep = (s) => {
    if (s === 1) return ['cliente_id'];
    if (s === 2) return ['peso', 'dim_comprimento', 'dim_largura', 'dim_altura'];
    if (s === 3) return ['valor_remessa'];
    if (s === 4) return ['conteudo'];
    if (s === 5) return ['origem', 'destino'];
    return [];
  };

  const handleNextStep = () => {
    const step = currentStep;
    const { valid, missingLabels, invalid } = getWizardStepValidation(step);

    if (!valid) {
      setInvalidFields((prev) => ({ ...prev, ...invalid }));
      showWarning(
        `Preencha os campos obrigatórios: ${missingLabels.join(', ')}.`
      );
      const firstKey = Object.keys(invalid)[0];
      if (firstKey === 'cliente_id') scrollToWizardField('field-cliente_id');
      else if (firstKey === 'peso') scrollToWizardField('field-peso');
      else if (firstKey === 'dim_comprimento') scrollToWizardField('field-dim-comprimento');
      else if (firstKey === 'dim_largura') scrollToWizardField('field-dim-largura');
      else if (firstKey === 'dim_altura') scrollToWizardField('field-dim-altura');
      else if (firstKey === 'valor_remessa') scrollToWizardField('field-valor_remessa');
      else if (firstKey === 'conteudo') scrollToWizardField('field-conteudo');
      else if (firstKey === 'origem') scrollToWizardField('field-origem');
      else if (firstKey === 'destino') scrollToWizardField('field-destino');
      return;
    }

    setInvalidFields((prev) => {
      const next = { ...prev };
      clearInvalidKeysForStep(step).forEach((k) => delete next[k]);
      return next;
    });

    if (step === 2 && !isBoxesAdmin) {
      const tid = String(DEFAULT_GERENTE_TARIFA_ID);
      setFormData((prev) => {
        const tarifasCalculadas = calculateTarifa(
          tid,
          prev.peso,
          prev.tarifa_intermediacao || '',
          prev.tarifa_coleta || ''
        );
        const tf = tarifas.find((t) => String(t.id) === tid);
        let vr =
          tarifasCalculadas.valor_remessa !== undefined
            ? tarifasCalculadas.valor_remessa
            : prev.valor_remessa;
        const pesoW = parseWeight(prev.peso);
        if (
          pesoW > 0 &&
          (!String(vr).trim() || parseCurrency(vr) <= 0)
        ) {
          vr = formatNumberAsCurrency(GERENTE_VALOR_REMESA_POR_KG * pesoW);
        }
        return {
          ...prev,
          tarifa_id: tid,
          tarifa_intermediacao: tarifasCalculadas.tarifa_intermediacao,
          tarifa_coleta: tarifasCalculadas.tarifa_coleta,
          valor_remessa: vr,
          origem: tf?.origem || prev.origem,
          destino: tf?.destino || prev.destino,
        };
      });
      setCurrentStep(4);
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let s = 1; s <= 5; s++) {
      const { valid, missingLabels, invalid } = getWizardStepValidation(s);
      if (!valid) {
        setInvalidFields((prev) => ({ ...prev, ...invalid }));
        const firstKey = Object.keys(invalid)[0];
        const goStep =
          s === 3 && !isBoxesAdmin && firstKey === 'valor_remessa' ? 2 : s;
        setCurrentStep(goStep);
        showWarning(
          `Preencha os campos obrigatórios: ${missingLabels.join(', ')}.`
        );
        if (firstKey === 'cliente_id') scrollToWizardField('field-cliente_id');
        else if (firstKey === 'peso') scrollToWizardField('field-peso');
        else if (firstKey === 'dim_comprimento') scrollToWizardField('field-dim-comprimento');
        else if (firstKey === 'dim_largura') scrollToWizardField('field-dim-largura');
        else if (firstKey === 'dim_altura') scrollToWizardField('field-dim-altura');
        else if (firstKey === 'valor_remessa')
          scrollToWizardField(!isBoxesAdmin ? 'field-peso' : 'field-valor_remessa');
        else if (firstKey === 'conteudo') scrollToWizardField('field-conteudo');
        else if (firstKey === 'origem') scrollToWizardField('field-origem');
        else if (firstKey === 'destino') scrollToWizardField('field-destino');
        return;
      }
    }

    try {
      setSaving(true);
      // Gerar código de acesso do cliente automaticamente se não existir (apenas para novas caixas)
      let codigosAcesso = formData.codigos_acesso;
      if (!editingId && (!codigosAcesso || !codigosAcesso.cliente)) {
        codigosAcesso = generateClientAccessCode();
      }

      // Garantir que codigos_acesso tem o formato correto com código do cliente
      codigosAcesso = {
        cliente: codigosAcesso?.cliente || ''
      };

      // Preparar itens com arquivos - manter estrutura com fotos para a API processar
      const itensComArquivos = (formData.itens || []).map((item) => {
        const itemData = {
          nome: item.nome || item.name || '',
          ncm: item.ncm || '',
          descricao: item.descricao || item.description || '',
          quantidade: item.quantidade || item.quantity || 1,
          peso: item.peso || item.weight ? parseWeight(item.peso || item.weight) : '',
          preco: item.preco || item.price || '',
          // Manter fotos no formato original (com file) para a API processar
          fotos: (item.fotos || []).map(photo => {
            // Se photo tem file, usar file, senão usar photo diretamente
            if (photo.file && photo.file instanceof File) {
              return { file: photo.file };
            } else if (photo instanceof File) {
              return { file: photo };
            }
            // Se não for File, pode ser uma URL (para edição), ignorar
            return null;
          }).filter(Boolean)
        };

        // Incluir ID do item se existir (para atualização, não duplicar)
        if (item.id) {
          itemData.id = item.id;
        }

        return itemData;
      });

      // Processar nota fiscal - extrair apenas os arquivos File
      let notaFiscalProcessada = null;
      if (formData.nota_fiscal) {
        if (Array.isArray(formData.nota_fiscal)) {
          // Se for array, pegar apenas os arquivos File
          const files = formData.nota_fiscal
            .map(nf => nf.file || nf)
            .filter(f => f instanceof File);
          notaFiscalProcessada = files.length > 0 ? files : null;
        } else if (formData.nota_fiscal instanceof File) {
          notaFiscalProcessada = formData.nota_fiscal;
        } else if (formData.nota_fiscal.file && formData.nota_fiscal.file instanceof File) {
          notaFiscalProcessada = formData.nota_fiscal.file;
        } else if (typeof formData.nota_fiscal === 'string') {
          // Se for string (URL), manter como está para edição
          notaFiscalProcessada = formData.nota_fiscal;
        }
      }

      // Converter dados do formulário para o formato esperado pela API
      // TODOS os campos devem ser enviados, mesmo opcionais (conforme especificação)

      // Validar tipo - garantir que seja um valor válido
      // A API aceita: 'caixa', 'mala', 'pallet', 'container'
      // Se o valor for inválido ou vazio, usar 'caixa' como padrão
      const validTypes = ['caixa', 'mala', 'pallet', 'container'];
      let tipoValue = 'caixa'; // Valor padrão

      if (formData.tipo && typeof formData.tipo === 'string') {
        const tipoLower = formData.tipo.toLowerCase().trim();
        if (validTypes.includes(tipoLower)) {
          tipoValue = tipoLower;
        }
      }

      let codigoPacote = formData.codigo;
      if (!editingId) {
        const codes = await loadAllCodesForSequence();
        packageCodesRef.current = codes;
        codigoPacote = nextPackageCodeForTipo(codes, tipoValue);
      }

      let valorDeclarado = formData.valor_remessa || '';
      if (!editingId && !isBoxesAdmin) {
        const pesoNum = formData.peso ? parseWeight(formData.peso) : 0;
        const v = parseCurrency(String(valorDeclarado));
        if (!String(valorDeclarado).trim() || v <= 0) {
          if (pesoNum > 0) {
            valorDeclarado = formatNumberAsCurrency(
              GERENTE_VALOR_REMESA_POR_KG * pesoNum
            );
          }
        }
      }

      const boxData = {
        codigo_pacote: codigoPacote,
        tipo: tipoValue,
        cliente_id: formData.cliente_id || '',
        status: formData.status || 'pedido_criado',
        comprimento: formData.dimensoes?.comprimento || '',
        largura: formData.dimensoes?.largura || '',
        altura: formData.dimensoes?.altura || '',
        peso_bruto: formData.peso ? parseWeight(formData.peso) : '',
        peso_liquido: formData.peso_liquido || '',
        valor_declarado: valorDeclarado,
        tarifa_id: editingId
          ? formData.tarifa_id || ''
          : isBoxesAdmin
            ? formData.tarifa_id || ''
            : String(DEFAULT_GERENTE_TARIFA_ID),
        tarifa_intermediacao: formData.tarifa_intermediacao || '',
        tarifa_coleta: formData.tarifa_coleta || '',
        conteudo: formData.conteudo || '',
        observacao: formData.observacao || '',
        origem: formData.origem || '',
        destino: formData.destino || '',
        nome_destinatario: formData.nome_destinatario || '',
        telefone_destinatario: formData.telefone_destinatario || '',
        pais_transito: formData.pais_transito || '',
        codigo_atualizacao: formData.codigo_atualizacao || '',
        tipo_pacote_id: formData.tipo_pacote_id || '',
        endereco_origin_id: formData.endereco_origin_id || '',
        endereco_destino_id: formData.endereco_destino_id || '',
        codigos_acesso: codigosAcesso,
        dimensoes: formData.dimensoes ? JSON.stringify(formData.dimensoes) : '',
        itens: itensComArquivos,
        nota_fiscal: notaFiscalProcessada
      };

      if (editingId) {
        await apiService.updateBox(editingId, boxData);
        showSuccess('Caixa/Mala atualizada com sucesso!');
      } else {
        await apiService.createBox(boxData);
        showSuccess('Caixa/Mala criada com sucesso!');
      }
      handleCloseModal();
      loadBoxes();
    } catch (error) {
      console.error('Erro ao salvar caixa/mala:', error);
      showWarning(error.message || 'Erro ao salvar caixa/mala');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (box) => {
    setFormData({
      ...box,
      // Mapear codigo_pacote para codigo
      codigo: box.codigo_pacote || box.codigo || '',
      // Mapear cliente.nome para cliente_nome
      cliente_nome: box.cliente?.nome || box.cliente_nome || '',
      // Mapear cliente_id
      cliente_id: box.cliente_id || box.cliente?.id || '',
      // Mapear dimensões separadas para objeto dimensoes
      dimensoes: {
        comprimento: box.comprimento || box.dimensoes?.comprimento || '',
        largura: box.largura || box.dimensoes?.largura || '',
        altura: box.altura || box.dimensoes?.altura || ''
      },
      // Mapear peso_bruto para peso
      peso: box.peso_bruto || box.peso || '',
      // Mapear valor_declarado para valor_remessa
      valor_remessa: box.valor_declarado || box.valor_remessa || '',
      // Mapear tarifa_id
      tarifa_id: box.tarifa_id || '',
      // Mapear tarifa_intermediacao e tarifa_coleta
      tarifa_intermediacao: box.tarifa_intermediacao || '',
      tarifa_coleta: box.tarifa_coleta || '',
      // Mapear origem e destino (garantir que sempre sejam strings)
      origem: box.origem || '',
      destino: box.destino || '',
      // Mapear nome_destinatario e telefone_destinatario
      nome_destinatario: box.nome_destinatario || '',
      telefone_destinatario: box.telefone_destinatario || '',
      // Mapear observacao
      observacao: box.observacao || '',
      // Garantir que codigos_acesso existe e está no formato correto
      codigos_acesso: (() => {
        let codigos = box.codigos_acesso;
        // Se for string JSON, fazer parse
        if (typeof codigos === 'string' && codigos.trim() !== '') {
          try {
            codigos = JSON.parse(codigos);
          } catch (e) {
            console.error('Erro ao fazer parse de codigos_acesso:', e);
            codigos = null;
          }
        }
        // Garantir formato padrão com código do cliente
        return codigos && typeof codigos === 'object' ? {
          cliente: codigos.cliente || ''
        } : {
          cliente: ''
        };
      })(),
      // Garantir que itens existe e formatar peso de cada item
      itens: (box.itens || []).map(item => ({
        ...item,
        peso: (() => {
          const pesoValue = item.peso || item.weight;
          if (!pesoValue) return '';
          // Se for número, multiplicar por 100 para converter para o formato da máscara
          if (typeof pesoValue === 'number') {
            return formatWeight(String(Math.round(pesoValue * 100)));
          }
          // Se for string, converter vírgula para ponto e formatar
          const pesoStr = String(pesoValue).replace(',', '.');
          const pesoNum = parseFloat(pesoStr);
          if (!isNaN(pesoNum)) {
            return formatWeight(String(Math.round(pesoNum * 100)));
          }
          return formatWeight(pesoStr);
        })()
      })),
      // Mapear nota_fiscal_url para nota_fiscal
      nota_fiscal: box.nota_fiscal_url || box.nota_fiscal || null
    });
    setEditingId(box.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!isBoxesAdmin) return;
    setBoxToDelete(id);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!isBoxesAdmin) return;
    try {
      await apiService.deleteBox(boxToDelete);
    setShowConfirmDialog(false);
    setBoxToDelete(null);
    showSuccess('Caixa/Mala excluída com sucesso!');
      loadBoxes();
    } catch (error) {
      console.error('Erro ao excluir caixa/mala:', error);
      showWarning(error.message || 'Erro ao excluir caixa/mala');
    }
  };

  // Função para converter valor formatado (R$ 200,00) para número decimal
  const parseCurrencyToNumber = (value) => {
    if (!value) return 0;
    // Remove R$, espaços, pontos (milhares) e converte vírgula para ponto
    const numberStr = value
      .toString()
      .replace(/[R$\s]/g, '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim();
    const parsed = parseFloat(numberStr);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleRegisterPayment = async () => {
    if (!boxPayment.valor || !boxPayment.data) {
      showWarning('Preencha todos os campos obrigatórios do pagamento');
      return;
    }

    if (!selectedBoxForPayment?.id) {
      showWarning('Caixa/Mala não selecionada');
      return;
    }

    try {
      // Converter valor formatado para número
      const valorNumerico = parseCurrencyToNumber(boxPayment.valor);

      if (valorNumerico <= 0) {
        showWarning('O valor do pagamento deve ser maior que zero');
        return;
      }

      // Converter para string no formato de moeda (ex: 873.00, 3400.00)
      // Garantir 2 casas decimais
      const valorFormatado = valorNumerico.toFixed(2);

      // Preparar dados para a API
      const paymentData = {
        pacote_id: selectedBoxForPayment.id,
        valor: valorFormatado, // Enviar como string no formato "873.00" ou "3400.00"
        metodo_pagamento_id: null, // Pode ser implementado depois se houver endpoint para buscar métodos
        metodo_pagamento: boxPayment.metodo || 'dinheiro', // Enviar método de pagamento
        moeda_id: null, // Pode ser implementado depois se houver endpoint para buscar moedas
        comprovante: boxPayment.comprovante || undefined
      };

      await apiService.createPayment(paymentData);

      showSuccess(`Pagamento de ${formatCurrency(boxPayment.valor)} registrado com sucesso para caixa/mala ${selectedBoxForPayment?.codigo || selectedBoxForPayment?.codigo_pacote || ''}`);

      // Recarregar boxes para atualizar status_pagamento
      await loadBoxes();

      setShowPaymentModal(false);
      setBoxPayment({
        valor: '',
        data: new Date().toISOString().split('T')[0],
        metodo: 'dinheiro',
        comprovante: null
      });
      setSelectedBoxForPayment(null);
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      showWarning(error?.message || 'Erro ao registrar pagamento. Tente novamente.');
    }
  };

  const handleCancelPayment = async () => {
    if (!cancelJustification || cancelJustification.trim().length < 10) {
      showWarning('A justificativa deve ter no mínimo 10 caracteres');
      return;
    }

    if (!selectedBoxForPayment?.id) {
      showWarning('Caixa/Mala não selecionada');
      return;
    }

    try {
      await apiService.cancelPayment({
        pacote_id: selectedBoxForPayment.id,
        justificativa_cancelamento: cancelJustification.trim()
      });

      showSuccess('Pagamento cancelado com sucesso!');

      // Recarregar boxes para atualizar status
      await loadBoxes();

      // Fechar modais
      setShowCancelPaymentModal(false);
      setShowViewPaymentModal(false);
      setCancelJustification('');
      setSelectedPayment(null);
      setSelectedBoxForPayment(null);
    } catch (error) {
      console.error('Erro ao cancelar pagamento:', error);
      showWarning(error?.message || 'Erro ao cancelar pagamento. Tente novamente.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentStep(1);
    setFormData({
      codigo: '',
      tipo: 'caixa',
      cliente_id: '',
      cliente_nome: '',
      peso: '',
      dimensoes: {
        comprimento: DEFAULT_DIMENSAO_CM,
        largura: DEFAULT_DIMENSAO_CM,
        altura: DEFAULT_DIMENSAO_CM
      },
      valor_remessa: '',
      tarifa_id: '',
      tarifa_intermediacao: '',
      tarifa_coleta: '',
      conteudo: '',
      observacao: '',
      status: 'pedido_criado',
      origem: '',
      destino: '',
      nome_destinatario: '',
      telefone_destinatario: '',
      pais_transito: '',
      codigo_rastreio: '',
      codigo_atualizacao: '',
      codigos_acesso: {
        cliente: ''
      },
      itens: [],
      nota_fiscal: null
    });
    setEditingId(null);
    setSelectedColetaId('');
    setCurrentItem({
      nome: '',
      ncm: '',
      descricao: '',
      peso: '',
      preco: '',
      quantidade: 1,
      fotos: []
    });
  };

  const handleAddItem = () => {
    // Validar campos obrigatórios
    if (!currentItem.nome) {
      validateAndHighlightField('item-nome', 'Preencha o nome do item');
      return;
    }
    if (!currentItem.peso) {
      validateAndHighlightField('item-peso', 'Preencha o peso do item');
      return;
    }
    if (!currentItem.preco) {
      validateAndHighlightField('item-preco', 'Preencha o preço do item');
      return;
    }

    // Limpar validações ao adicionar item com sucesso
    setInvalidFields({});

    // Processar fotos antes de adicionar o item
    const processedFotos = (currentItem.fotos || []).map(photo => {
      // Se a foto tem um file, manter o file
      // Se tem preview mas não file, tentar manter o preview
      // Remover referências a blob URLs que podem ser inválidas
      if (photo.file && photo.file instanceof File) {
        return {
          id: photo.id || Date.now() + Math.random(),
          file: photo.file,
          name: photo.name || photo.file.name,
          preview: photo.preview // Manter preview se existir
        };
      }
      // Se não tem file, mas tem preview válido, manter apenas o preview
      if (photo.preview && typeof photo.preview === 'string') {
        return {
          id: photo.id || Date.now() + Math.random(),
          name: photo.name || 'Foto',
          preview: photo.preview
        };
      }
      return null;
    }).filter(photo => photo !== null);

    const newItem = {
      id: Date.now(),
      nome: currentItem.nome || '',
      ncm: currentItem.ncm || '',
      descricao: currentItem.descricao || '',
      peso: currentItem.peso || '',
      preco: currentItem.preco || '',
      quantidade: parseInt(currentItem.quantidade) || 1,
      fotos: processedFotos
    };

    setFormData({
      ...formData,
      itens: [...(formData.itens || []), newItem]
    });

    setCurrentItem({
      nome: '',
      ncm: '',
      descricao: '',
      peso: '',
      preco: '',
      quantidade: 1,
      fotos: []
    });
    setNcmSuggestions([]);
    setShowNcmDropdown(false);
  };

  // Função para comprimir imagem
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.6) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar se necessário
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Converter para blob comprimido
          canvas.toBlob((blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Funções para câmera
  const handleOpenCamera = (forItem = false, forNotaFiscal = false, forComprovante = false) => {
    setCameraForItem(forItem);
    setCameraForNotaFiscal(forNotaFiscal);
    setCameraForComprovante(forComprovante);
    setShowCameraModal(true);
  };

  const handleCloseCamera = () => {
    setShowCameraModal(false);
    if (videoRef.current) {
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    try {
      // Parar stream anterior se existir
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacingMode }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      showWarning('Não foi possível acessar a câmera. Verifique as permissões.');
      handleCloseCamera();
    }
  };

  const toggleCamera = async () => {
    setCameraFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    // A câmera será reiniciada no useEffect quando cameraFacingMode mudar
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Ajustar dimensões do canvas para o vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Desenhar frame do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Converter canvas para blob e depois para File (com compressão)
    canvas.toBlob(async (blob) => {
      if (blob) {
        const originalFile = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' });

        // Comprimir a imagem
        const compressedFile = await compressImage(originalFile, 1920, 1920, 0.6);

        const uploadedFile = {
          id: Date.now() + Math.random(),
          file: compressedFile,
          preview: URL.createObjectURL(compressedFile),
          name: compressedFile.name
        };

        if (cameraForItem) {
          // Adicionar foto ao item atual
          setCurrentItem({ ...currentItem, fotos: [...(currentItem.fotos || []), uploadedFile] });
        } else if (cameraForNotaFiscal) {
          // Adicionar nota fiscal
          const notaFiscalFile = {
            id: Date.now() + Math.random(),
            file: compressedFile,
            preview: URL.createObjectURL(compressedFile),
            name: compressedFile.name
          };
          setFormData({
            ...formData,
            nota_fiscal: formData.nota_fiscal
              ? [...(Array.isArray(formData.nota_fiscal) ? formData.nota_fiscal : [formData.nota_fiscal]), notaFiscalFile]
              : [notaFiscalFile]
          });
        } else if (cameraForComprovante) {
          // Adicionar comprovante
          setBoxPayment({ ...boxPayment, comprovante: compressedFile });
        }

        showSuccess('Foto capturada e comprimida com sucesso!');
        handleCloseCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  useEffect(() => {
    if (showCameraModal && videoRef.current) {
      startCamera();
    }
    return () => {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, [showCameraModal, cameraFacingMode]);



  const handleNotaFiscalUpload = (files) => {
    // Se já são objetos processados, usar diretamente
    if (Array.isArray(files) && files.length > 0 && files[0].id) {
      setFormData({
        ...formData,
        nota_fiscal: formData.nota_fiscal
          ? [...(Array.isArray(formData.nota_fiscal) ? formData.nota_fiscal : [formData.nota_fiscal]), ...files]
          : files
      });
    } else {
      // Se são File objects, processar
      const uploadedFiles = Array.from(files).map(file => {
        return {
          id: Date.now() + Math.random(),
          file,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
          name: file.name
        };
      });
      setFormData({
        ...formData,
        nota_fiscal: formData.nota_fiscal
          ? [...(Array.isArray(formData.nota_fiscal) ? formData.nota_fiscal : [formData.nota_fiscal]), ...uploadedFiles]
          : uploadedFiles
      });
    }
  };

  const handleRemoveNotaFiscal = (fileId) => {
    if (Array.isArray(formData.nota_fiscal)) {
      setFormData({
        ...formData,
        nota_fiscal: formData.nota_fiscal.filter(file => file.id !== fileId)
      });
    } else {
      setFormData({ ...formData, nota_fiscal: null });
    }
  };

  const getStatusIconEl = (status) => {
    if (!status) status = 'pedido_criado';
    const statusInfo = getStatusInfo(status);
    const statusColor = getStatusColor(status);
    const iconName = getStatusIcon(status);
    const IconComponent = HiIcons[iconName] || HiIcons.HiClock;
    return (
      <span
        title={statusInfo.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: statusColor,
          cursor: 'default'
        }}
      >
        <IconComponent size={20} />
      </span>
    );
  };

  // Paginação é feita na API; boxes já contém apenas os itens da página atual
  const totalPages = lastPage;
  const startIndex = totalBoxes > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIndex = Math.min(currentPage * itemsPerPage, totalBoxes);
  const invoiceRowClassByKey = useMemo(
    () => buildInvoiceRowClassMap(boxes),
    [boxes]
  );
  const destinoTextClassByKey = useMemo(
    () => buildDestinoTextClassMap(boxes),
    [boxes]
  );
  const codigoClienteRecipient = selectedBoxRecipient
    ? getClienteCodigoFromBox(selectedBoxRecipient)
    : '';

  const handleSaveRecipientCardAsImage = async () => {
    const node = recipientPrintAreaRef.current;
    if (!node) {
      showWarning('Área do cartão não encontrada.');
      return;
    }
    try {
      setSavingRecipientImage(true);
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff'
      });
      const link = document.createElement('a');
      const pacote = (
        selectedBoxRecipient?.codigo_pacote ||
        selectedBoxRecipient?.codigo ||
        ''
      )
        .toString()
        .replace(/[^\w-]/g, '_');
      const cliente = (codigoClienteRecipient || 'destinatario')
        .toString()
        .replace(/[^\w-]/g, '_')
        .slice(0, 48);
      link.download = `retirada-${pacote || cliente}.png`;
      link.href = dataUrl;
      link.click();
      showSuccess('Imagem baixada com sucesso.');
    } catch (err) {
      showWarning(err?.message || 'Não foi possível gerar a imagem. Tente novamente.');
    } finally {
      setSavingRecipientImage(false);
    }
  };

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Caixas / Malas</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Caixas / Malas</h1>
          <p>Gerencie caixas e malas para envio</p>
        </div>
        {canCreateEditBoxes && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOpenModal}
            disabled={loadingSequenceCodes}
          >
            <HiPlus size={20} />
            {loadingSequenceCodes ? 'Carregando…' : 'Nova Caixa/Mala'}
          </button>
        )}
      </div>

      <div className="page-actions">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar caixas/malas..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={isBoxesAdmin ? 13 : 12} />
        ) : !Array.isArray(boxes) || boxes.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? 'Nenhuma caixa/mala encontrada' : 'Nenhuma caixa/mala cadastrada'}</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Invoice</th>
                    <th>Criado</th>
                    <th>Destino</th>
                    <th>Peso</th>
                    <th>Dimensões</th>
                    {isBoxesAdmin && <th>Valor</th>}
                    <th>Status</th>
                    <th>NF</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {boxes.map((box, index) => {
                    const invKey = getBoxInvoiceKey(box);
                    const invRowClass = invKey ? invoiceRowClassByKey.get(invKey) : '';
                    const destinoKey = getDestinoGroupKey(box);
                    const destinoTextClass =
                      destinoTextClassByKey.get(destinoKey) || 'boxes-destino-text--empty';
                    const destinoDisplay =
                      box.destino && String(box.destino).trim() !== ''
                        ? String(box.destino).trim()
                        : '—';
                    return (
                    <tr key={box.id} className={invRowClass || undefined}>
                      <td>{startIndex + index}</td>
                      <td><strong>{box.codigo || box.codigo_pacote || 'N/A'}</strong></td>
                      <td>{box.tipo === 'caixa' ? '📦 Caixa' : '🧳 Mala'}</td>
                      <td>
                        {box.cliente_nome ||
                         box.cliente?.nome ||
                         box.cliente?.name ||
                         box.cliente_name ||
                         (box.cliente && typeof box.cliente === 'string' ? box.cliente : null) ||
                         'N/A'}
                      </td>
                      <td className="boxes-table-cell-invoice">
                        {getBoxInvoiceLabel(box) ? (
                          <span
                            className="boxes-table-invoice-badge"
                            title={invKey ? `Invoice #${invKey}` : undefined}
                          >
                            {getBoxInvoiceLabel(box)}
                          </span>
                        ) : (
                          <span className="boxes-table-invoice-empty">—</span>
                        )}
                      </td>
                      <td className="boxes-table-cell-datetime">
                        {formatBoxCreatedAt(box.created_at)}
                      </td>
                      <td className="boxes-table-cell-destino">
                        <strong className={destinoTextClass}>{destinoDisplay}</strong>
                      </td>
                      <td>{box.peso || box.peso_bruto || 'N/A'}</td>
                      <td>
                        {(box.dimensoes?.comprimento || box.comprimento) ? `${box.dimensoes?.comprimento || box.comprimento}cm` : '-'} x {(box.dimensoes?.largura || box.largura) ? `${box.dimensoes?.largura || box.largura}cm` : '-'} x {(box.dimensoes?.altura || box.altura) ? `${box.dimensoes?.altura || box.altura}cm` : '-'}
                      </td>
                      {isBoxesAdmin && (
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <strong>{displayCurrency(box.valor_remessa || box.valor_declarado)}</strong>
                            {box.status_pagamento && (
                              <span style={{
                                fontSize: '0.75rem',
                                color: box.status_pagamento?.toLowerCase() === 'pago' ? '#10b981' :
                                       box.status_pagamento?.toLowerCase() === 'pendente' ? '#f59e0b' :
                                       box.status_pagamento?.toLowerCase() === 'cancelado' ? '#ef4444' : '#6b7280',
                                fontWeight: 500
                              }}>
                                {box.status_pagamento?.toLowerCase() === 'pago' ? '✓ Pago' :
                                 box.status_pagamento?.toLowerCase() === 'pendente' ? '○ Pendente' :
                                 box.status_pagamento?.toLowerCase() === 'cancelado' ? '✕ Cancelado' :
                                 box.status_pagamento}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td>{getStatusIconEl(box.status)}</td>
                      <td>
                        {(() => {
                          let notaFiscalUrl = null;
                          if (Array.isArray(box.nota_fiscal_url) && box.nota_fiscal_url.length > 0) {
                            notaFiscalUrl = box.nota_fiscal_url[0];
                          } else if (typeof box.nota_fiscal_url === 'string' && box.nota_fiscal_url.trim() !== '') {
                            notaFiscalUrl = box.nota_fiscal_url;
                          } else if (box.nota_fiscal && typeof box.nota_fiscal === 'string' && box.nota_fiscal.trim() !== '') {
                            notaFiscalUrl = box.nota_fiscal;
                          }

                          const hasNotaFiscal = notaFiscalUrl && typeof notaFiscalUrl === 'string' && notaFiscalUrl.trim() !== '';

                          if (!hasNotaFiscal) {
                            return (
                              <span
                                className="btn-icon"
                                title="Nota Fiscal não disponível"
                                style={{ color: '#9ca3af', cursor: 'not-allowed', opacity: 0.5 }}
                              >
                                <HiEye size={18} />
                              </span>
                            );
                          }

                          const isPDF = typeof notaFiscalUrl === 'string' && (
                            notaFiscalUrl.match(/\.(pdf)$/i) ||
                            notaFiscalUrl.toLowerCase().includes('application/pdf') ||
                            notaFiscalUrl.toLowerCase().includes('pdf')
                          );

                          const isImage = typeof notaFiscalUrl === 'string' && !isPDF && (
                            notaFiscalUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) ||
                            notaFiscalUrl.match(/^data:image\//) ||
                            notaFiscalUrl.toLowerCase().includes('image/') ||
                            notaFiscalUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)\?/i)
                          );

                          if (isImage) {
                            return (
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => {
                                  setSelectedNotaFiscal(notaFiscalUrl);
                                  setShowNotaFiscalModal(true);
                                }}
                                title="Ver Nota Fiscal"
                                style={{ color: '#3b82f6' }}
                              >
                                <HiEye size={18} />
                              </button>
                            );
                          }

                          return (
                            <a
                              href={notaFiscalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-icon"
                              title="Ver Nota Fiscal"
                              style={{ color: '#3b82f6', textDecoration: 'none' }}
                            >
                              <HiEye size={18} />
                            </a>
                          );
                        })()}
                      </td>
                      <td>
                        <div className="actions">
                          {canCreateEditBoxes && (
                            <button
                              type="button"
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(box)}
                              title="Editar"
                            >
                              <HiPencil size={18} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => navigate(`/labels?box=${box.id}`)}
                            title="Gerar Etiqueta"
                          >
                            <HiPrinter size={18} />
                          </button>
                          <div className="actions-menu-wrapper" style={{ position: 'relative' }}>
                            <button
                              type="button"
                              className="btn-icon btn-menu-toggle"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openMenuId === box.id) {
                                  setOpenMenuId(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const menuHeight = 300;
                                  const menuWidth = 200;
                                  const viewportHeight = window.innerHeight;
                                  const viewportWidth = window.innerWidth;
                                  let top = rect.bottom + 5;
                                  if (top + menuHeight > viewportHeight) {
                                    top = rect.top - menuHeight - 5;
                                    if (top < 0) top = Math.max(10, (viewportHeight - menuHeight) / 2);
                                  }
                                  let right = viewportWidth - rect.right;
                                  if (right + menuWidth > viewportWidth) {
                                    right = viewportWidth - rect.left - menuWidth;
                                    if (right < 0) right = (viewportWidth - menuWidth) / 2;
                                  }
                                  setMenuPosition({ top, right });
                                  setOpenMenuId(box.id);
                                }
                              }}
                              title="Mais opções"
                            >
                              <HiDotsVertical size={18} />
                            </button>
                            {openMenuId === box.id && (
                              <>
                                <div
                                  className="actions-dropdown-overlay"
                                  onClick={() => setOpenMenuId(null)}
                                />
                                <div
                                  className="actions-dropdown-menu"
                                  style={{
                                    top: `${menuPosition.top}px`,
                                    right: `${menuPosition.right}px`,
                                    transform: 'none'
                                  }}
                                >
                                <button
                                  className="dropdown-menu-item"
                                  onClick={() => {
                                    let codigos = box.codigos_acesso;
                                    // Se for string JSON, fazer parse
                                    if (typeof codigos === 'string' && codigos.trim() !== '') {
                                      try {
                                        codigos = JSON.parse(codigos);
                                      } catch (e) {
                                        console.error('Erro ao fazer parse de codigos_acesso:', e);
                                        codigos = null;
                                      }
                                    }
                                    // Garantir formato padrão com código do cliente
                                    const codigosFormatados = codigos && typeof codigos === 'object' ? {
                                      cliente: codigos.cliente || ''
                                    } : {
                                      cliente: ''
                                    };

                                    setSelectedBoxCodes({
                                      ...box,
                                      codigo: box.codigo_pacote || box.codigo || '',
                                      codigos_acesso: codigosFormatados
                                    });
                              setShowCodesModal(true);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <HiKey size={16} />
                                  <span>Códigos de Acesso</span>
                          </button>
                          <button
                                  className="dropdown-menu-item"
                                  onClick={() => {
                                    setSelectedBoxItems(box);
                                    setShowItemsModal(true);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <HiCollection size={16} />
                                  <span>Listar Itens</span>
                                </button>
                                {box.entregas && Array.isArray(box.entregas) && box.entregas.length > 0 && (
                                  <button
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                      setSelectedBoxDelivery(box);
                                      setShowDeliveryModal(true);
                                      setOpenMenuId(null);
                                    }}
                                  >
                                    <HiLocationMarker size={16} />
                                    <span>Dados da Entrega</span>
                                  </button>
                                )}
                                <button
                                  className="dropdown-menu-item"
                                  onClick={() => {
                                    setSelectedBoxRecipient(box);
                                    setShowRecipientModal(true);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <HiEye size={16} />
                                  <span>Ver Destinatário</span>
                                </button>
                                <button
                                  className="dropdown-menu-item"
                                  onClick={async () => {
                                    setOpenMenuId(null);

                                    // Verificar se box.status_pagamento é "Pago" ou "Cancelado" - se sim, buscar e mostrar pagamento
                                    const statusPagamento = box.status_pagamento ? box.status_pagamento.toLowerCase() : '';
                                    if (statusPagamento === 'pago' || statusPagamento === 'cancelado') {
                                      try {
                                        const payment = await apiService.getPaymentByPackage(box.id);
                                        if (payment && payment.data) {
                                          // Mostrar modal de visualização de pagamento
                                          setSelectedPayment(payment.data);
                                          setSelectedBoxForPayment(box);
                                          setShowViewPaymentModal(true);
                                        } else {
                                          // Se não encontrou pagamento mas status é pago, permitir abrir modal de registro
                                          let valorPreenchido = '';
                                          const valorDeclarado = box.valor_declarado || box.valor_remessa || '';
                                          if (valorDeclarado) {
                                            let numero = 0;
                                            if (typeof valorDeclarado === 'string') {
                                              const numeroStr = valorDeclarado.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
                                              numero = parseFloat(numeroStr) || 0;
                                            } else {
                                              numero = parseFloat(valorDeclarado) || 0;
                                            }
                                            if (numero > 0) {
                                              valorPreenchido = formatCurrency(String(Math.round(numero * 100)));
                                            }
                                          }
                                          setSelectedBoxForPayment(box);
                                          setBoxPayment({
                                            valor: valorPreenchido,
                                            data: new Date().toISOString().split('T')[0],
                                            metodo: 'dinheiro',
                                            comprovante: null
                                          });
                                          setShowPaymentModal(true);
                                        }
                                      } catch (error) {
                                        console.error('Erro ao buscar pagamento:', error);
                                        // Em caso de erro ao buscar, permitir abrir modal de registro
                                        let valorPreenchido = '';
                                        const valorDeclarado = box.valor_declarado || box.valor_remessa || '';
                                        if (valorDeclarado) {
                                          let numero = 0;
                                          if (typeof valorDeclarado === 'string') {
                                            const numeroStr = valorDeclarado.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
                                            numero = parseFloat(numeroStr) || 0;
                                          } else {
                                            numero = parseFloat(valorDeclarado) || 0;
                                          }
                                          if (numero > 0) {
                                            valorPreenchido = formatCurrency(String(Math.round(numero * 100)));
                                          }
                                        }
                                        setSelectedBoxForPayment(box);
                                        setBoxPayment({
                                          valor: valorPreenchido,
                                          data: new Date().toISOString().split('T')[0],
                                          metodo: 'dinheiro',
                                          comprovante: null
                                        });
                                        setShowPaymentModal(true);
                                      }
                                    } else {
                                        // Abrir modal de registro de pagamento
                                        // Converter valor_declarado para formato do input
                                        let valorPreenchido = '';
                                        const valorDeclarado = box.valor_declarado || box.valor_remessa || '';
                                        if (valorDeclarado) {
                                          // Converter valor_declarado para número (removendo formatação)
                                          let numero = 0;
                                          if (typeof valorDeclarado === 'string') {
                                            // Remover R$, pontos, espaços e converter vírgula para ponto
                                            const numeroStr = valorDeclarado.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
                                            numero = parseFloat(numeroStr) || 0;
                                          } else {
                                            numero = parseFloat(valorDeclarado) || 0;
                                          }
                                          if (numero > 0) {
                                            // Multiplicar por 100 porque formatCurrency divide por 100
                                            // formatCurrency espera receber "20000" para formatar como "R$ 200,00"
                                            valorPreenchido = formatCurrency(String(Math.round(numero * 100)));
                                          }
                                        }
                                        setSelectedBoxForPayment(box);
                                        setBoxPayment({
                                          valor: valorPreenchido,
                                          data: new Date().toISOString().split('T')[0],
                                          metodo: 'dinheiro',
                                          comprovante: null
                                        });
                                        setShowPaymentModal(true);
                                    }
                                  }}
                                >
                                  {box.status_pagamento && (box.status_pagamento.toLowerCase() === 'pago' || box.status_pagamento.toLowerCase() === 'cancelado') ? (
                                    <>
                                      <HiEye size={16} />
                                      <span>Ver Pagamento</span>
                                    </>
                                  ) : (
                                    <>
                                      <HiCash size={16} />
                                      <span>Registrar Pagamento</span>
                                    </>
                                  )}
                                </button>
                                <div className="dropdown-divider"></div>
                                {isBoxesAdmin && (
                                  <button
                                    className="dropdown-menu-item danger"
                                    onClick={() => {
                                      handleDelete(box.id);
                                      setOpenMenuId(null);
                                    }}
                                  >
                                    <HiTrash size={16} />
                                    <span>Excluir</span>
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="pagination-info">
                {totalBoxes > 0 ? (
                  <span>
                    Mostrando {startIndex} a {endIndex} de {totalBoxes} registro{totalBoxes !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className="items-per-page">
                <span>Mostrar:</span>
                <Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    const perPage = Number(e.target.value);
                    setItemsPerPage(perPage);
                    setCurrentPage(1);
                    loadBoxes({ page: 1, per_page: perPage });
                  }}
                  className="select-sm"
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                />
              </div>
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => {
                    const prevPage = Math.max(1, currentPage - 1);
                    setCurrentPage(prevPage);
                    loadBoxes({ page: prevPage });
                  }}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => {
                    const nextPage = Math.min(totalPages, currentPage + 1);
                    setCurrentPage(nextPage);
                    loadBoxes({ page: nextPage });
                  }}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar Caixa/Mala' : 'Nova Caixa/Mala'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <HiX size={24} />
              </button>
            </div>

            {/* Wizard Progress Indicator */}
            <div ref={wizardProgressRef} className="wizard-progress">
              <div className="wizard-steps">
                {wizardStepsDisplay.map(({ step, label }, idx) => (
                  <div
                    key={step}
                    className={`wizard-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''} ${editingId ? 'clickable' : ''}`}
                    onClick={() => {
                      if (editingId) {
                        setCurrentStep(step);
                      }
                    }}
                    style={{
                      cursor: editingId ? 'pointer' : 'default'
                    }}
                    role={editingId ? 'button' : undefined}
                    tabIndex={editingId ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (editingId && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setCurrentStep(step);
                      }
                    }}
                  >
                    <div className="wizard-step-circle">
                      {currentStep > step ? '✓' : isBoxesAdmin ? step : idx + 1}
                    </div>
                    <div className="wizard-step-label">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <form ref={modalFormRef} onSubmit={handleSubmit} className="modal-form">
              <div className="wizard-content">
                {/* Etapa 1: Informações Básicas */}
                <div className={`wizard-step-content ${currentStep === 1 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Informações Básicas</h3>
                  {!editingId && (
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label>Coleta (opcional)</label>
                        <Select
                          value={selectedColetaId}
                          onChange={(e) => handleColetaPrefillChange(e.target.value)}
                          options={[
                            {
                              value: '',
                              label: 'Nenhuma — preencher manualmente',
                            },
                            ...coletasForModal.map((c) => ({
                              value: String(c.id),
                              label: `${c.codigo || c.codigo_pacote || '—'} — ${c.cliente_nome || 'Cliente'}`,
                            })),
                          ]}
                        />
                        <small className="input-hint">
                          Ao escolher uma coleta, cliente, peso, tarifa, origem, destino e destinatário são
                          preenchidos quando disponíveis; você pode ajustar o restante nas próximas etapas.
                        </small>
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Código</label>
                      <input
                        type="text"
                        value={formData.codigo}
                        readOnly
                        className="readonly-input"
                        placeholder="Gerado automaticamente"
                      />
                      <small className="input-hint">Código gerado automaticamente</small>
                    </div>
                    <div className="form-group">
                      <label>Tipo</label>
                      <Select
                        value={formData.tipo}
                        onChange={(e) => {
                          const newTipo = e.target.value;
                          void (async () => {
                            try {
                              let codes = packageCodesRef.current;
                              if (!codes || codes.length === 0) {
                                codes = await loadAllCodesForSequence();
                                packageCodesRef.current = codes;
                              }
                              const newCode = nextPackageCodeForTipo(codes, newTipo);
                              setFormData((prev) => ({
                                ...prev,
                                tipo: newTipo,
                                codigo: newCode,
                              }));
                            } catch (err) {
                              showWarning(
                                err?.message ||
                                  'Não foi possível recalcular o código para o tipo selecionado.'
                              );
                            }
                          })();
                        }}
                        required
                        options={[
                          { value: 'caixa', label: 'Caixa' },
                          { value: 'mala', label: 'Mala' },
                          { value: 'pallet', label: 'Pallet' },
                          { value: 'container', label: 'Container' }
                        ]}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Cliente {labelReq}
                      </label>
                      <ClientSearchSelect
                        id="field-cliente_id"
                        value={formData.cliente_id}
                        selectedNome={formData.cliente_nome}
                        clients={clients}
                        onRefreshClients={loadClients}
                        onNotify={({ type, message }) => {
                          if (type === 'success') showSuccess(message);
                          else showWarning(message);
                        }}
                        onChange={({ cliente_id, cliente_nome }) => {
                          setFormData({
                            ...formData,
                            cliente_id,
                            cliente_nome,
                          });
                          if (invalidFields['cliente_id']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['cliente_id'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="Selecione ou busque um cliente"
                        invalid={!!invalidFields['cliente_id']}
                        disabled={saving}
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <Select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        required
                        options={STATUS_LIST.map(s => ({
                          value: s.value,
                          label: s.label
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 2: Dimensões e Peso */}
                <div className={`wizard-step-content ${currentStep === 2 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Dimensões e Peso</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Peso bruto (kg) {labelReq}
                      </label>
                      <input
                        id="field-peso"
                        type="text"
                        value={formData.peso}
                        onChange={(e) => {
                          handlePesoChange(e.target.value);
                          // Limpar validação ao digitar
                          if (invalidFields['peso']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['peso'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="15"
                        className={invalidFields['peso'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Comprimento (cm) {labelReq}
                      </label>
                      <input
                        id="field-dim-comprimento"
                        type="text"
                        value={formData.dimensoes.comprimento}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            dimensoes: { ...formData.dimensoes, comprimento: e.target.value }
                          });
                          if (invalidFields['dim_comprimento']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.dim_comprimento;
                              return n;
                            });
                          }
                        }}
                        placeholder="50"
                        className={invalidFields['dim_comprimento'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Largura (cm) {labelReq}
                      </label>
                      <input
                        id="field-dim-largura"
                        type="text"
                        value={formData.dimensoes.largura}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            dimensoes: { ...formData.dimensoes, largura: e.target.value }
                          });
                          if (invalidFields['dim_largura']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.dim_largura;
                              return n;
                            });
                          }
                        }}
                        placeholder="40"
                        className={invalidFields['dim_largura'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Altura (cm) {labelReq}
                      </label>
                      <input
                        id="field-dim-altura"
                        type="text"
                        value={formData.dimensoes.altura}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            dimensoes: { ...formData.dimensoes, altura: e.target.value }
                          });
                          if (invalidFields['dim_altura']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.dim_altura;
                              return n;
                            });
                          }
                        }}
                        placeholder="30"
                        className={invalidFields['dim_altura'] ? 'field-invalid' : ''}
                      />
                    </div>
                  </div>
                  {!isBoxesAdmin && (
                    <p className="input-hint" style={{ marginTop: '0.75rem' }}>
                      Ao avançar, o valor da remessa será calculado com a tarifa padrão (base {DEFAULT_GERENTE_TARIFA_ID}). Só o administrador vê e edita a etapa de tarifas.
                    </p>
                  )}
                </div>

                {isBoxesAdmin && (
                <div className={`wizard-step-content ${currentStep === 3 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Tarifas e Valores</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Tarifa</label>
                      <Select
                        value={formData.tarifa_id}
                        onChange={(e) => handleTarifaChange(e.target.value)}
                        options={[
                          { value: '', label: 'Selecione uma tarifa' },
                          ...tarifas.map(tarifa => {
                            // Formatar valor da tarifa
                            const valorFormatado = tarifa.valor
                              ? typeof tarifa.valor === 'number'
                                ? tarifa.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : parseFloat(tarifa.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : '0,00';
                            return {
                              value: tarifa.id,
                              label: `${tarifa.nome} - ${tarifa.origem} → ${tarifa.destino} (R$ ${valorFormatado})`
                            };
                          })
                        ]}
                      />
                    </div>
                    <div className="form-group">
                      <label>Taxa Intermediação</label>
                      <input
                        type="text"
                        value={formData.tarifa_intermediacao || ''}
                        onChange={(e) => handleTarifaIntermediacaoChange(e.target.value)}
                        placeholder="R$ 0,00"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Taxa Coleta</label>
                      <input
                        type="text"
                        value={formData.tarifa_coleta || ''}
                        onChange={(e) => handleTarifaColetaChange(e.target.value)}
                        placeholder="R$ 0,00"
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Valor da Remessa {labelReq}
                      </label>
                      <input
                        id="field-valor_remessa"
                        type="text"
                        value={formData.valor_remessa}
                        onChange={(e) => {
                          const formatted = formatCurrency(e.target.value);
                          setFormData({ ...formData, valor_remessa: formatted });
                          if (invalidFields['valor_remessa']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.valor_remessa;
                              return n;
                            });
                          }
                        }}
                        placeholder="R$ 0,00"
                        className={invalidFields['valor_remessa'] ? 'field-invalid' : ''}
                      />
                    </div>
                  </div>
                </div>
                )}

                {/* Etapa 4: Conteúdo e Observações */}
                <div className={`wizard-step-content ${currentStep === 4 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Conteúdo e Observações</h3>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>
                        Conteúdo {labelReq}
                      </label>
                      <input
                        id="field-conteudo"
                        type="text"
                        value={formData.conteudo}
                        onChange={(e) => {
                          setFormData({ ...formData, conteudo: e.target.value });
                          if (invalidFields['conteudo']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.conteudo;
                              return n;
                            });
                          }
                        }}
                        placeholder="Descrição do conteúdo"
                        className={invalidFields['conteudo'] ? 'field-invalid' : ''}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Observação</label>
                      <textarea
                        value={formData.observacao}
                        onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                        placeholder="Observações adicionais"
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 5: Origem e Destino */}
                <div className={`wizard-step-content ${currentStep === 5 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Origem e Destino</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Origem {labelReq}
                      </label>
                      <input
                        id="field-origem"
                        type="text"
                        value={formData.origem || ''}
                        onChange={(e) => {
                          setFormData({ ...formData, origem: e.target.value });
                          if (invalidFields['origem']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.origem;
                              return n;
                            });
                          }
                        }}
                        placeholder="Cidade/País de origem"
                        disabled={isBoxesAdmin && !!formData.tarifa_id}
                        className={invalidFields['origem'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Destino {labelReq}
                      </label>
                      <input
                        id="field-destino"
                        type="text"
                        value={formData.destino || ''}
                        onChange={(e) => {
                          setFormData({ ...formData, destino: e.target.value });
                          if (invalidFields['destino']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.destino;
                              return n;
                            });
                          }
                        }}
                        placeholder="Cidade/País de destino"
                        disabled={isBoxesAdmin && !!formData.tarifa_id}
                        className={invalidFields['destino'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>País de Trânsito</label>
                      <input
                        type="text"
                        value={formData.pais_transito}
                        onChange={(e) => setFormData({ ...formData, pais_transito: e.target.value })}
                        placeholder="País de trânsito (opcional)"
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 6: Destinatário */}
                <div className={`wizard-step-content ${currentStep === 6 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Destinatário</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Nome Destinatário</label>
                      <input
                        id="field-nome_destinatario"
                        type="text"
                        value={formData.nome_destinatario || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, nome_destinatario: e.target.value })
                        }
                        placeholder="Nome do destinatário"
                      />
                    </div>
                    <div className="form-group">
                      <label>Telefone Destinatário</label>
                      <input
                        id="field-telefone_destinatario"
                        type="text"
                        value={formData.telefone_destinatario || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, telefone_destinatario: e.target.value })
                        }
                        placeholder="Telefone do destinatário"
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 7: Itens */}
                <div className={`wizard-step-content ${currentStep === 7 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)', fontSize: '1.125rem', fontWeight: '600' }}>Itens da Caixa/Mala</h3>

                {/* Formulário para adicionar novo item */}
                <div className="item-form">
                  <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label>Nome do Item *</label>
                      <input
                        id="field-item-nome"
                        type="text"
                        value={currentItem.nome || ''}
                        onChange={handleItemNameChange}
                        placeholder="Nome do item"
                        className={invalidFields['item-nome'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group" style={{ position: 'relative' }}>
                      <label>NCM</label>
                      <input
                        type="text"
                        value={currentItem.ncm || ''}
                        onChange={(e) => {
                          setCurrentItem({ ...currentItem, ncm: e.target.value });
                          setShowNcmDropdown(false);
                        }}
                        onFocus={() => {
                          if (ncmSuggestions.length > 0) {
                            setShowNcmDropdown(true);
                          }
                        }}
                        placeholder="1234.56.78"
                        style={{ position: 'relative' }}
                      />
                      {showNcmDropdown && ncmSuggestions.length > 0 && (
                        <div
                          className="ncm-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            marginTop: '0.25rem'
                          }}
                        >
                          {ncmSuggestions.map((ncm, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                setCurrentItem({ ...currentItem, ncm });
                                setShowNcmDropdown(false);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                borderBottom: index < ncmSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                                transition: 'background-color 0.2s',
                                fontSize: '0.875rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              {ncm}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Descrição do Produto</label>
                      <input
                        type="text"
                        value={currentItem.descricao || ''}
                        onChange={(e) => setCurrentItem({ ...currentItem, descricao: e.target.value })}
                        placeholder="Descrição detalhada do produto"
                      />
                    </div>
                    <div className="form-group">
                      <label>Peso do item (kg) *</label>
                      <input
                        id="field-item-peso"
                        type="text"
                        value={currentItem.peso || ''}
                        onChange={(e) => {
                          const formatted = formatWeight(e.target.value);
                          setCurrentItem({ ...currentItem, peso: formatted });
                          // Limpar validação ao digitar
                          if (invalidFields['item-peso']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['item-peso'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="0.5"
                        className={invalidFields['item-peso'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>Preço Unitário do Item (R$) *</label>
                      <input
                        id="field-item-preco"
                        type="text"
                        value={currentItem.preco || ''}
                        onChange={(e) => {
                          const formatted = formatCurrency(e.target.value);
                          setCurrentItem({ ...currentItem, preco: formatted });
                          // Limpar validação ao digitar
                          if (invalidFields['item-preco']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['item-preco'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="R$ 0,00"
                        className={invalidFields['item-preco'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantidade</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={currentItem.quantidade || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || parseInt(value) >= 1) {
                            setCurrentItem({ ...currentItem, quantidade: value === '' ? '' : parseInt(value) || 1 });
                          }
                        }}
                        placeholder="1"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ width: '100%', gridColumn: '1 / -1' }}>
                      <label>Fotos do Item</label>
                      <div className="photo-options" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap', marginBottom: '12px', width: '100%' }}>
                        <button
                          type="button"
                          className="btn-camera"
                          onClick={() => handleOpenCamera(true, false, false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <HiCamera size={20} />
                          Tirar Foto
                        </button>
                        <span style={{ color: '#666', whiteSpace: 'nowrap' }}>ou</span>
                        <label
                          className="btn-select-file"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <HiCloudUpload size={20} />
                          Galeria
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={async (e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                const files = Array.from(e.target.files);
                                const compressedFiles = await Promise.all(
                                  files.map(async (file) => {
                                    const compressed = await compressImage(file);
                                    return {
                                      id: Date.now() + Math.random(),
                                      file: compressed,
                                      preview: URL.createObjectURL(compressed),
                                      name: compressed.name
                                    };
                                  })
                                );
                                setCurrentItem({ ...currentItem, fotos: [...(currentItem.fotos || []), ...compressedFiles] });
                              }
                            }}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                      {currentItem.fotos && currentItem.fotos.length > 0 && (
                        <div className="photo-preview-list">
                          {currentItem.fotos.map((photo, idx) => {
                            // Verificar se preview é válido (não é blob URL inválido)
                            const previewUrl = photo.preview && typeof photo.preview === 'string' && photo.preview.startsWith('blob:')
                              ? photo.preview
                              : (photo.file ? URL.createObjectURL(photo.file) : null);

                            return (
                              <div key={photo.id || `current-item-photo-${idx}`} className="photo-preview-item">
                                {previewUrl ? (
                                  <img
                                    src={previewUrl}
                                    alt={photo.name || 'Foto'}
                                    onError={(e) => {
                                      // Se der erro, tentar criar novo blob URL
                                      if (photo.file && photo.file instanceof File) {
                                        try {
                                          e.target.src = URL.createObjectURL(photo.file);
                                        } catch (err) {
                                          e.target.style.display = 'none';
                                        }
                                      } else {
                                        e.target.style.display = 'none';
                                      }
                                    }}
                                  />
                                ) : (
                                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Sem preview</div>
                                )}
                                <button
                                  type="button"
                                  className="btn-icon btn-delete-small"
                                  onClick={() => {
                                    // Revogar blob URL antes de remover
                                    if (previewUrl && previewUrl.startsWith('blob:')) {
                                      try {
                                        URL.revokeObjectURL(previewUrl);
                                      } catch (e) {
                                        // Ignorar erros ao revogar
                                      }
                                    }
                                    const updatedFotos = currentItem.fotos.filter((_, i) => i !== idx);
                                    setCurrentItem({ ...currentItem, fotos: updatedFotos });
                                  }}
                                  style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                >
                                  <HiX size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-row" style={{ justifyContent: 'flex-end' }}>
                    <div className="form-group">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleAddItem}>
                        <HiPlus size={16} />
                        Confirmar Item
                      </button>
                    </div>
                  </div>
                </div>

                {/* Lista de itens adicionados */}
                {formData.itens && formData.itens.length > 0 && (
                  <div className="items-list">
                    {formData.itens.map((item, itemIndex) => (
                      <div key={item.id || `item-${itemIndex}`} className="item-card">
                        <div className="item-header">
                          <div className="item-info">
                            <strong>{item.nome}</strong>
                            {(item.ncm || item.descricao) && (
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                {item.ncm && <span>NCM: {item.ncm}</span>}
                                {item.ncm && item.descricao && <span> | </span>}
                                {item.descricao && <span>{item.descricao}</span>}
                              </div>
                            )}
                            <span>Qtd: {item.quantidade} | Peso: {item.peso}kg | Preço: {displayCurrency(item.preco)}</span>
                          </div>
                        </div>
                        {item.fotos && item.fotos.length > 0 && (
                          <div className="item-photos">
                            <label>Fotos do Item:</label>
                            <div className="photo-preview-list">
                              {item.fotos.map((photo, photoIdx) => {
                                // Tratar diferentes formatos de foto: {url}, {preview}, string direto, ou File
                                const rawPhotoUrl = photo?.url || photo?.preview || (typeof photo === 'string' ? photo : null);
                                // Normalizar URL da imagem (remover barras invertidas e adicionar public/ se necessário)
                                const photoUrl = rawPhotoUrl ? normalizeImageUrl(rawPhotoUrl) : null;
                                const photoId = photo?.id || `item-${itemIndex}-photo-${photoIdx}`;

                                return (
                                  <div key={photoId} className="photo-preview-item">
                                    {photoUrl ? (
                                      <img
                                        src={photoUrl}
                                        alt={`Foto ${photoIdx + 1} do item ${item.nome || item.name || ''}`}
                                        onError={(e) => {
                                          // Tentar recriar blob URL se for um File
                                          if (photo.file && photo.file instanceof File) {
                                            try {
                                              const newUrl = URL.createObjectURL(photo.file);
                                              e.target.src = newUrl;
                                              return;
                                            } catch (err) {
                                              // Se falhar, ocultar a imagem
                                            }
                                          }
                                          // Se não conseguir recriar, ocultar a imagem
                                          e.target.style.display = 'none';
                                          // Adicionar mensagem de erro de forma segura (sem innerHTML)
                                          const errorDiv = document.createElement('div');
                                          errorDiv.style.cssText = 'display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 0.75rem;';
                                          errorDiv.textContent = 'Erro ao carregar';
                                          if (e.target.parentElement) {
                                            e.target.parentElement.appendChild(errorDiv);
                                          }
                                        }}
                                      />
                                    ) : (
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', fontSize: '0.75rem' }}>
                                        Sem preview
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                </div>

                {/* Etapa 8: Nota Fiscal */}
                <div className={`wizard-step-content ${currentStep === 8 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Nota Fiscal</h3>
                <div className="form-group">
                  <label>Upload de Nota Fiscal</label>
                  <div className="photo-options" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap', marginBottom: '12px' }}>
                    <button
                      type="button"
                      className="btn-camera"
                      onClick={() => handleOpenCamera(false, true, false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <HiCamera size={20} />
                      Tirar Foto
                    </button>
                    <span style={{ color: '#666', whiteSpace: 'nowrap' }}>ou</span>
                    <label
                      htmlFor="nota-fiscal-upload"
                      className="file-upload-label"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <HiCloudUpload size={20} />
                      Galeria
                    </label>
                    <input
                      type="file"
                      id="nota-fiscal-upload"
                      multiple
                      accept="image/*,.pdf,application/pdf"
                      onChange={async (e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const files = Array.from(e.target.files);
                          const processedFiles = await Promise.all(
                            files.map(async (file) => {
                              // Se for imagem, comprimir; se for PDF, manter como está
                              if (file.type.startsWith('image/')) {
                                const compressed = await compressImage(file);
                                return {
                                  id: Date.now() + Math.random(),
                                  file: compressed,
                                  preview: URL.createObjectURL(compressed),
                                  name: compressed.name
                                };
                              } else {
                                return {
                                  id: Date.now() + Math.random(),
                                  file,
                                  preview: null,
                                  name: file.name
                                };
                              }
                            })
                          );
                          handleNotaFiscalUpload(processedFiles);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {formData.nota_fiscal && (
                    <div className="nota-fiscal-list">
                      {Array.isArray(formData.nota_fiscal) ? (
                        formData.nota_fiscal.map((file, fileIdx) => (
                          <div key={file.id || `nota-fiscal-${fileIdx}`} className="nota-fiscal-item">
                            <div className="file-info">
                              <HiDocument size={20} />
                              <span>{file.name}</span>
                            </div>
                            {file.preview && file.preview.match(/\.(jpg|jpeg|png|gif)$/i) && (
                              <img src={file.preview} alt={file.name} className="nota-fiscal-preview" />
                            )}
                            <button
                              type="button"
                              className="btn-icon btn-delete"
                              onClick={() => handleRemoveNotaFiscal(file.id)}
                              title="Remover"
                            >
                              <HiTrash size={18} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="nota-fiscal-item">
                          <div className="file-info">
                            <HiDocument size={20} />
                            <span>{formData.nota_fiscal.name || 'Nota Fiscal'}</span>
                          </div>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => setFormData({ ...formData, nota_fiscal: null })}
                            title="Remover"
                          >
                            <HiTrash size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Wizard Navigation */}
              <div className="wizard-actions">
                <div className="wizard-actions-left">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={saving}
                    style={{
                      minWidth: '120px',
                      padding: '0.75rem 1.5rem',
                      color: '#ffffff',
                      backgroundColor: '#6b7280',
                      borderColor: '#6b7280'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
                <div className="wizard-actions-right">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handlePrevStep}
                      disabled={saving}
                      style={{
                        minWidth: '120px',
                        padding: '0.75rem 1.5rem',
                        color: '#ffffff',
                        backgroundColor: '#6b7280',
                        borderColor: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      <HiChevronLeft size={18} style={{ color: '#ffffff' }} />
                      <span style={{ color: '#ffffff' }}>Anterior</span>
                    </button>
                  )}
                  {currentStep < totalSteps && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNextStep();
                      }}
                      disabled={saving || currentStep >= totalSteps}
                      style={{
                        minWidth: '120px',
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      <span>Próximo</span>
                      <HiChevronRight size={18} />
                    </button>
                  )}
                  {(editingId || currentStep === totalSteps) && (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                      style={{
                        minWidth: '120px',
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {saving ? (
                        <>
                          <HiRefresh size={18} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
                          <span>{editingId ? 'Atualizando...' : 'Salvando...'}</span>
                        </>
                      ) : (
                        <span>{editingId ? 'Atualizar' : 'Salvar'} Caixa/Mala</span>
                      )}
                    </button>
                  )}
                </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Nota Fiscal */}
      {showNotaFiscalModal && selectedNotaFiscal && (
        <div className="modal-overlay" onClick={() => setShowNotaFiscalModal(false)}>
          <div className="modal-content" style={{ maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nota Fiscal</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowNotaFiscalModal(false);
                  setSelectedNotaFiscal(null);
                }}
                title="Fechar"
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1rem', overflow: 'auto' }}>
              {(() => {
                // O modal só deve abrir para imagens, então aqui só renderizamos imagens
                // Verificar se é PDF primeiro (para garantir que não é)
                const isPDF = typeof selectedNotaFiscal === 'string' && (
                  selectedNotaFiscal.match(/\.(pdf)$/i) ||
                  selectedNotaFiscal.toLowerCase().includes('application/pdf') ||
                  selectedNotaFiscal.toLowerCase().includes('pdf')
                );

                // Verificar se é imagem (e não é PDF)
                const isImage = typeof selectedNotaFiscal === 'string' &&
                  !isPDF && (
                    selectedNotaFiscal.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) ||
                    selectedNotaFiscal.match(/^data:image\//) ||
                    selectedNotaFiscal.toLowerCase().includes('image/') ||
                    selectedNotaFiscal.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)\?/i) // Com query params
                  );

                if (isImage) {
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <img
                        src={selectedNotaFiscal}
                        alt="Nota Fiscal"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '70vh',
                          height: 'auto',
                          display: 'block',
                          margin: '0 auto',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2rem; text-align: center;">
                              <p style="margin-bottom: 1rem;">Não foi possível carregar a imagem.</p>
                              <a href="${selectedNotaFiscal}" target="_blank" style="color: #3b82f6; text-decoration: underline;">
                                Clique aqui para abrir em nova aba
                              </a>
                            </div>
                          `;
                        }}
                      />
                      <a
                        href={selectedNotaFiscal}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          textDecoration: 'none',
                          marginTop: '0.5rem'
                        }}
                      >
                        <HiExternalLink size={16} />
                        Abrir em Nova Aba
                      </a>
                    </div>
                  );
                } else {
                  // Fallback: tentar exibir como PDF ou oferecer download
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', minHeight: '400px' }}>
                      <p style={{ marginBottom: '1rem', color: '#666' }}>
                        Visualização não disponível para este tipo de arquivo.
                      </p>
                      <a
                        href={selectedNotaFiscal}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '0.375rem',
                          fontWeight: '600'
                        }}
                      >
                        Abrir arquivo em nova aba
                      </a>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Códigos de Acesso */}
      {showCodesModal && selectedBoxCodes && (
        <div className="modal-overlay" onClick={() => setShowCodesModal(false)}>
          <div className="modal-content codes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Códigos de Acesso - {selectedBoxCodes.codigo}</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowCodesModal(false);
                  setSelectedBoxCodes(null);
                }}
                title="Fechar"
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="codes-list">
                <div className="code-row">
                  <div className="code-label">
                    <strong>Cliente</strong>
                  </div>
                  <div className="code-content">
                    <span className="code-text">{selectedBoxCodes.codigos_acesso?.cliente || 'Não gerado'}</span>
                    {selectedBoxCodes.codigos_acesso?.cliente && (
                      <button
                        className="btn-copy-code"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedBoxCodes.codigos_acesso.cliente);
                          showSuccess('Código copiado!');
                        }}
                        title="Copiar código"
                      >
                        <HiClipboardCopy size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCodesModal(false);
                    setSelectedBoxCodes(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Itens da Caixa */}
      {showItemsModal && selectedBoxItems && (
        <div className="modal-overlay" onClick={() => setShowItemsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Itens da Caixa - {selectedBoxItems.codigo || selectedBoxItems.codigo_pacote}</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowItemsModal(false);
                  setSelectedBoxItems(null);
                }}
                title="Fechar"
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              {selectedBoxItems.itens && selectedBoxItems.itens.length > 0 ? (
                <div className="items-list">
                  {selectedBoxItems.itens.map((item, index) => (
                    <div key={item.id || index} className="item-detail-card">
                      <div className="item-detail-header">
                        <div className="item-detail-info">
                          <h4>{item.nome || item.name || `Item ${index + 1}`}</h4>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                            {item.ncm && <span><strong>NCM:</strong> {item.ncm}</span>}
                            {item.ncm && item.descricao && <span> | </span>}
                            {item.descricao && <span><strong>Descrição:</strong> {item.descricao}</span>}
                          </div>
                          <div className="item-detail-meta">
                            <span><strong>Quantidade:</strong> {item.quantidade || item.quantity || 'N/A'}</span>
                            <span><strong>Peso:</strong> {item.peso || item.weight || 'N/A'} {item.peso || item.weight ? 'kg' : ''}</span>
                            <span><strong>Preço:</strong> {item.preco ? displayCurrency(item.preco) : item.price ? displayCurrency(item.price) : 'N/A'}</span>
                          </div>
                        </div>
                        {isBoxesAdmin && (
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => {
                              setItemToDelete(item);
                              setShowDeleteItemDialog(true);
                            }}
                            title="Excluir item"
                          >
                            <HiTrash size={18} />
                          </button>
                        )}
                      </div>
                      {item.fotos && Array.isArray(item.fotos) && item.fotos.length > 0 && (
                        <div className="item-detail-photos">
                          <label>Fotos do Item ({item.fotos.length}):</label>
                          <div className="photo-gallery">
                            {item.fotos.map((photo, photoIdx) => {
                              // Tratar diferentes formatos de foto: {url}, {preview}, string direto
                              const rawPhotoUrl = photo?.url || photo?.preview || (typeof photo === 'string' ? photo : null);
                              const photoId = photo?.id || `item-${index}-photo-${photoIdx}`;

                              if (!rawPhotoUrl) return null;

                              // Normalizar URL da imagem (remover barras invertidas e adicionar public/ se necessário)
                              const photoUrl = normalizeImageUrl(rawPhotoUrl);

                              return (
                                <div key={photoId} className="photo-gallery-item">
                                  <img
                                    src={photoUrl}
                                    alt={`Foto ${photoIdx + 1} do item ${item.nome || item.name || ''}`}
                                    onClick={() => window.open(photoUrl, '_blank')}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 0.75rem; padding: 0.5rem;">Erro ao carregar imagem</div>';
                                    }}
                                  />
                                  <div className="photo-overlay">
                                    <button
                                      type="button"
                                      className="photo-view-btn"
                                      onClick={() => window.open(photoUrl, '_blank')}
                                      title="Abrir em nova aba"
                                    >
                                      <HiEye size={18} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>Nenhum item cadastrado nesta caixa</p>
                </div>
              )}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowItemsModal(false);
                    setSelectedBoxItems(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta caixa/mala?"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setBoxToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal de Registro de Pagamento */}
      {showPaymentModal && selectedBoxForPayment && (
        <div
          className="modal-overlay"
          onClick={() => setShowPaymentModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registrar Pagamento</h2>
              <button
                className="modal-close"
                onClick={() => setShowPaymentModal(false)}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Caixa/Mala</label>
                <input
                  type="text"
                  value={`${selectedBoxForPayment.codigo || selectedBoxForPayment.codigo_pacote || ''} - ${selectedBoxForPayment.cliente_nome || selectedBoxForPayment.cliente?.nome || ''}`}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Valor</label>
                <input
                  type="text"
                  value={boxPayment.valor}
                  onChange={(e) => {
                    const formatted = formatCurrency(e.target.value);
                    setBoxPayment({ ...boxPayment, valor: formatted });
                  }}
                  required
                  placeholder="R$ 0,00"
                />
              </div>
              <div className="form-group">
                <label>Data do Pagamento</label>
                <input
                  type="date"
                  value={boxPayment.data}
                  onChange={(e) =>
                    setBoxPayment({ ...boxPayment, data: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Método de Pagamento</label>
                <Select
                  value={boxPayment.metodo}
                  onChange={(e) =>
                    setBoxPayment({ ...boxPayment, metodo: e.target.value })
                  }
                  required
                  options={[
                    { value: 'dinheiro', label: 'Dinheiro' },
                    { value: 'pix', label: 'PIX' },
                    { value: 'cartao', label: 'Cartão' },
                    { value: 'transferencia', label: 'Transferência' },
                  ]}
                />
              </div>
              <div className="form-group" style={{ width: '100%', minWidth: '100%' }}>
                <label>Comprovante</label>
                <div className="photo-options" style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  marginBottom: '12px',
                  width: '100%',
                  minWidth: '100%',
                  overflow: 'hidden'
                }}>
                  <button
                    type="button"
                    className="btn-camera"
                    onClick={() => handleOpenCamera(false, false, true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    <HiCamera size={20} />
                    Tirar Foto
                  </button>
                  <span style={{ color: '#666', whiteSpace: 'nowrap', flexShrink: 0 }}>ou</span>
                  <label
                    htmlFor="payment-receipt-upload"
                    className="file-upload-label"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    <HiCloudUpload size={20} />
                    Galeria
                  </label>
                  <input
                    type="file"
                    id="payment-receipt-upload"
                    accept="image/*,.pdf"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Se for imagem, comprimir; se for PDF, manter como está
                        if (file.type.startsWith('image/')) {
                          const compressed = await compressImage(file);
                          setBoxPayment({ ...boxPayment, comprovante: compressed });
                        } else {
                          setBoxPayment({ ...boxPayment, comprovante: file });
                        }
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </div>
                {boxPayment.comprovante && (
                  <div className="file-preview" style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f3f4f6', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                      {boxPayment.comprovante.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setBoxPayment({ ...boxPayment, comprovante: null })}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}
                      title="Remover arquivo"
                    >
                      <HiX size={18} />
                    </button>
                  </div>
                )}
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  Formatos aceitos: Imagem (JPG, PNG) ou PDF
                </small>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedBoxForPayment(null);
                    setBoxPayment({
                      valor: '',
                      data: new Date().toISOString().split('T')[0],
                      metodo: 'dinheiro',
                      comprovante: null
                    });
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRegisterPayment}
                >
                  <HiCheckCircle size={18} />
                  Registrar Pagamento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Pagamento */}
      {showViewPaymentModal && selectedPayment && selectedBoxForPayment && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowViewPaymentModal(false);
            setSelectedPayment(null);
            setSelectedBoxForPayment(null);
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Pagamento Registrado</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowViewPaymentModal(false);
                  setSelectedPayment(null);
                  setSelectedBoxForPayment(null);
                }}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Caixa/Mala</label>
                <input
                  type="text"
                  value={`${selectedBoxForPayment.codigo || selectedBoxForPayment.codigo_pacote || ''} - ${selectedBoxForPayment.cliente_nome || selectedBoxForPayment.cliente?.nome || ''}`}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Valor</label>
                <input
                  type="text"
                  value={selectedPayment.valor ? displayCurrency(selectedPayment.valor) : 'N/A'}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              {(selectedPayment.data_pagamento || selectedPayment.created_at) && (
                <div className="form-group">
                  <label>Data do Pagamento</label>
                  <input
                    type="text"
                    value={selectedPayment.data_pagamento
                      ? new Date(selectedPayment.data_pagamento).toLocaleDateString('pt-BR')
                      : selectedPayment.created_at
                      ? new Date(selectedPayment.created_at).toLocaleDateString('pt-BR')
                      : 'N/A'}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {(selectedBoxForPayment.status_pagamento && selectedBoxForPayment.status_pagamento.toLowerCase() === 'cancelado') && (
                <>
                  {selectedBoxForPayment.data_cancelamento && (
                    <div className="form-group">
                      <label>Data do Cancelamento</label>
                      <input
                        type="text"
                        value={new Date(selectedBoxForPayment.data_cancelamento).toLocaleString('pt-BR', {
                          timeZone: 'America/Sao_Paulo',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                        disabled
                        style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                    </div>
                  )}
                  {selectedBoxForPayment.usuario_cancelamento && (
                    <div className="form-group">
                      <label>Cancelado por</label>
                      <input
                        type="text"
                        value={selectedBoxForPayment.usuario_cancelamento.nome || 'N/A'}
                        disabled
                        style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                    </div>
                  )}
                </>
              )}
              {selectedPayment.metodo_pagamento && (
                <div className="form-group">
                  <label>Método de Pagamento</label>
                  <input
                    type="text"
                    value={selectedPayment.metodo_pagamento}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {selectedPayment.moeda && (
                <div className="form-group">
                  <label>Moeda</label>
                  <input
                    type="text"
                    value={selectedPayment.moeda}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {selectedPayment.tipo && (
                <div className="form-group">
                  <label>Tipo</label>
                  <input
                    type="text"
                    value={selectedPayment.tipo}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {selectedPayment.descricao && (
                <div className="form-group">
                  <label>Descrição</label>
                  <input
                    type="text"
                    value={selectedPayment.descricao}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {selectedPayment.comprovante_url && (
                <div className="form-group">
                  <label>Comprovante</label>
                  <div>
                    {(() => {
                      // Construir URL completa do comprovante
                      let comprovanteUrl = selectedPayment.comprovante_url;
                      if (comprovanteUrl && !comprovanteUrl.startsWith('http://') && !comprovanteUrl.startsWith('https://')) {
                        // Se é caminho relativo, adicionar base da API
                        const baseUrl = 'https://apicargo.sendingto.com';
                        comprovanteUrl = comprovanteUrl.startsWith('/')
                          ? `${baseUrl}${comprovanteUrl}`
                          : `${baseUrl}/${comprovanteUrl}`;
                      }
                      return (
                        <a
                          href={comprovanteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'underline' }}
                        >
                          Ver Comprovante
                        </a>
                      );
                    })()}
                  </div>
                </div>
              )}
              {selectedPayment.created_at && (
                <div className="form-group">
                  <label>Data de Registro</label>
                  <input
                    type="text"
                    value={`${new Date(selectedPayment.created_at).toLocaleDateString('pt-BR')} ${new Date(selectedPayment.created_at).toLocaleTimeString('pt-BR')}`}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {(selectedPayment.justificativa_cancelamento || (selectedBoxForPayment.status_pagamento && selectedBoxForPayment.status_pagamento.toLowerCase() === 'cancelado')) && (
                <div className="form-group">
                  <label>Justificativa do Cancelamento</label>
                  <textarea
                    value={selectedPayment.justificativa_cancelamento || 'N/A'}
                    disabled
                    rows={4}
                    style={{
                      background: '#f3f4f6',
                      cursor: 'not-allowed',
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              )}
              <div className="modal-actions">
                {selectedBoxForPayment.status_pagamento && selectedBoxForPayment.status_pagamento.toLowerCase() !== 'cancelado' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      setShowCancelPaymentModal(true);
                    }}
                    style={{ marginRight: 'auto' }}
                  >
                    <HiX size={18} />
                    Cancelar Pagamento
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowViewPaymentModal(false);
                    setSelectedPayment(null);
                    setSelectedBoxForPayment(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cancelamento de Pagamento */}
      {showCancelPaymentModal && selectedBoxForPayment && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowCancelPaymentModal(false);
            setCancelJustification('');
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cancelar Pagamento</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowCancelPaymentModal(false);
                  setCancelJustification('');
                }}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Caixa/Mala</label>
                <input
                  type="text"
                  value={`${selectedBoxForPayment.codigo || selectedBoxForPayment.codigo_pacote || ''} - ${selectedBoxForPayment.cliente_nome || selectedBoxForPayment.cliente?.nome || ''}`}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Justificativa do Cancelamento *</label>
                <textarea
                  value={cancelJustification}
                  onChange={(e) => setCancelJustification(e.target.value)}
                  placeholder="Informe o motivo do cancelamento (mínimo 10 caracteres)"
                  rows={4}
                  required
                  minLength={10}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  Mínimo de 10 caracteres. {cancelJustification.length > 0 && (
                    <span style={{ color: cancelJustification.length < 10 ? '#ef4444' : '#10b981' }}>
                      {cancelJustification.length}/10 caracteres
                    </span>
                  )}
                </small>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCancelPaymentModal(false);
                    setCancelJustification('');
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCancelPayment}
                  disabled={!cancelJustification || cancelJustification.trim().length < 10}
                >
                  <HiX size={18} />
                  Confirmar Cancelamento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteItemDialog}
        title="Confirmar Exclusão de Item"
        message={`Tem certeza que deseja excluir o item "${itemToDelete?.nome || itemToDelete?.name || 'este item'}"? Esta ação não pode ser desfeita e removerá todas as fotos e vínculos associados.`}
        onConfirm={async () => {
          if (itemToDelete?.id) {
            try {
              await apiService.deleteProduct(itemToDelete.id);
              showSuccess('Item excluído com sucesso!');

              // Atualizar a lista de itens no modal
              if (selectedBoxItems && selectedBoxItems.itens) {
                const updatedItens = selectedBoxItems.itens.filter(item => item.id !== itemToDelete.id);
                setSelectedBoxItems({
                  ...selectedBoxItems,
                  itens: updatedItens
                });
              }

              // Recarregar os dados da caixa para refletir as mudanças
              await loadBoxes();

              setShowDeleteItemDialog(false);
              setItemToDelete(null);
            } catch (error) {
              console.error('Erro ao excluir item:', error);
              showWarning(error?.response?.data?.message || error?.message || 'Erro ao excluir item. Tente novamente.');
            }
          }
        }}
        onCancel={() => {
          setShowDeleteItemDialog(false);
          setItemToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal Ver Destinatário */}
      {showRecipientModal && selectedBoxRecipient && (
        <div className="modal-overlay recipient-print-modal-overlay" onClick={() => setShowRecipientModal(false)}>
          <div className="modal-content recipient-print-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 'min(100%, 28rem)', width: '90%' }}>
            <div className="modal-header recipient-print-no-print">
              <h3>Dados do Destinatário</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowRecipientModal(false)}
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body recipient-print-modal-body">
              <div
                ref={recipientPrintAreaRef}
                id="recipient-print-area"
                className="recipient-print-capture-wrap"
              >
                <div className="recipient-print-sheet">
                  <p className="recipient-print-sheet-title">Informações de Retirada</p>
                  <div className="recipient-print-sheet-inner">
                  <div className="recipient-print-logo-top">
                    <img
                      src="/SENDING_TO.png"
                      alt="SendingTo Express"
                      className="recipient-print-logo"
                    />
                  </div>
                  <div className="recipient-print-sheet-main">
                    <strong className="recipient-print-label">Nome do Destinatário:</strong>
                    <span className="recipient-print-value recipient-print-value-name">
                      {selectedBoxRecipient.nome_destinatario || 'Não informado'}
                    </span>
                  </div>
                  <div className="recipient-print-block">
                    <strong className="recipient-print-label">Código do Cliente:</strong>
                    <span className="recipient-print-value recipient-print-value-code">
                      {codigoClienteRecipient || 'Não informado'}
                    </span>
                    {codigoClienteRecipient ? (
                      <div className="recipient-print-qr-wrap" aria-hidden="true">
                        <QRCodeSVG
                          value={codigoClienteRecipient}
                          size={160}
                          level="M"
                          includeMargin={false}
                          className="recipient-print-qr"
                        />
                      </div>
                    ) : null}
                  </div>
                  {(() => {
                    const tarifa = tarifas.find(t => t.id === selectedBoxRecipient.tarifa_id || t.id === parseInt(selectedBoxRecipient.tarifa_id));
                    return (
                      <>
                        {tarifa && tarifa.destino && tarifa.destino.toLowerCase() === 'bissau' && (
                          <div className="recipient-print-local">
                            <strong className="recipient-print-local-title">Local da Retirada:</strong>
                            <p className="recipient-print-local-text">
                              Chapa de Bissau, subida da Cabana ao lado da Farmácia Mara.
                            </p>
                            <p className="recipient-print-local-text">
                              <strong>Contato do local:</strong> Edimilson: 955139049
                            </p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer recipient-print-no-print" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', padding: '1rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowRecipientModal(false)}
              >
                Fechar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveRecipientCardAsImage}
                disabled={savingRecipientImage}
              >
                <HiDownload size={18} />
                {savingRecipientImage ? 'Gerando…' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Dados da Entrega */}
      {showDeliveryModal && selectedBoxDelivery && (
        <div className="modal-overlay" onClick={() => setShowDeliveryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h3>Dados da Entrega - {selectedBoxDelivery.codigo_pacote || selectedBoxDelivery.codigo || selectedBoxDelivery.id}</h3>
              <button
                className="btn-icon"
                onClick={() => setShowDeliveryModal(false)}
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              {selectedBoxDelivery.entregas && Array.isArray(selectedBoxDelivery.entregas) && selectedBoxDelivery.entregas.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {selectedBoxDelivery.entregas.map((entrega, idx) => (
                    <div
                      key={idx}
                      style={{
                        backgroundColor: '#f9fafb',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <h4 style={{ marginBottom: '1rem', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
                        Entrega #{idx + 1}
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                        {entrega.codigo_cliente && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Código do Cliente:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>{entrega.codigo_cliente}</span>
                          </div>
                        )}
                        {entrega.numero_documento && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Número do Documento:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>{entrega.numero_documento}</span>
                          </div>
                        )}
                        {entrega.codigo_acesso && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Código de Acesso:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>{entrega.codigo_acesso}</span>
                          </div>
                        )}
                        {entrega.created_at && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Data/Hora:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>
                              {new Date(entrega.created_at).toLocaleString('pt-BR')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        {entrega.foto_saida_url && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Foto da Saída:</strong>
                            <img
                              src={normalizeImageUrl(entrega.foto_saida_url)}
                              alt="Foto da Saída"
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(normalizeImageUrl(entrega.foto_saida_url), '_blank')}
                            />
                          </div>
                        )}
                        {entrega.foto_assinatura_url && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Foto da Assinatura:</strong>
                            <img
                              src={normalizeImageUrl(entrega.foto_assinatura_url)}
                              alt="Foto da Assinatura"
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(normalizeImageUrl(entrega.foto_assinatura_url), '_blank')}
                            />
                          </div>
                        )}
                        {entrega.foto_documento_url && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Foto do Documento:</strong>
                            <img
                              src={normalizeImageUrl(entrega.foto_documento_url)}
                              alt="Foto do Documento"
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(normalizeImageUrl(entrega.foto_documento_url), '_blank')}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Nenhuma entrega registrada para este pacote.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Câmera */}
      {showCameraModal && (
        <div className="modal-overlay" onClick={handleCloseCamera}>
          <div className="modal-content camera-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', width: '90%' }}>
            <div className="modal-header">
              <h3>Tirar Foto do Item</h3>
              <button
                className="btn-icon"
                onClick={handleCloseCamera}
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1rem' }}>
              <div className="camera-container" style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="camera-video"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="camera-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '0.5rem 0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#6b7280';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiX size={18} />
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={toggleCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#17A2B8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  title={cameraFacingMode === 'environment' ? 'Usar câmera frontal' : 'Usar câmera traseira'}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#138496';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#17A2B8';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiRefresh size={18} />
                  {cameraFacingMode === 'environment' ? 'Frontal' : 'Traseira'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={capturePhoto}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '140px',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <HiCamera size={18} />
                  Capturar Foto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Boxes;
